import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    Output,
    EventEmitter,
    ChangeDetectorRef,
    ViewChild,
    AfterViewInit,
    OnDestroy,
    Input
} from '@angular/core';
import {ApgPortfolio} from "../../adjustment-pricing-grid/model/ApgPortfolio";
import {HedgePositionsService} from "../positions-section/hedge-positions/hedge-positions.service";
import {
    arraysEqual,
    delay,
    DetectMethodChanges, DetectSetterChanges,
    DxValueChanged,
    findAtmStrikeIndex, findHCF,
    getShortUUID, getStrikeStep, isArray, isFuturesTicker, isOptionExpired, isValidNumber,
    isVoid, makeGuiFriendlyExpirationDate, makePortfolioKey, snapToStrikeStep,
} from "../../utils";
import {HedgePositionModel} from "../positions-section/hedge-positions/hedge-position.model";
import {TradingInstrument} from "../../trading-instruments/trading-instrument.class";
import {TradingInstrumentsService} from "../../trading-instruments/trading-instruments-service.interface";
import * as Enumerable from "linq";
import {OptionsChainService} from "../../option-chains.service";
import {makeOptionTicker, OptionStrategy, OptionType, parseOptionTicker} from "../../options-common/options.model";
import {isNullOrUndefined} from "util";
import {LastQuoteCacheService} from "../../last-quote-cache.service";
import {SettingsStorageService} from "../../settings-storage-service.service";
import {ToastrService} from "ngx-toastr";
import {getIdentity, HedgePosition} from "../data-model/hedge-position";
import {MessageBusService} from "../../message-bus.service";
import {BeforePositionDto} from "../../adjustment-pricing-grid/model/BeforePositionDto";
import {
    HedgePackageToMerge,
    HgMergeDialogComponent,
    HgMergeDialogConfig
} from "./merge-dialog/hg-merge-dialog.component";
import {OptionExpirationDescriptor} from "../../shell-communication/shell-dto-protocol";
import {filter, takeUntil, throttleTime} from 'rxjs/operators';
import {from, Observable, Subject} from "rxjs";
import {DxNumberBoxComponent} from "devextreme-angular/ui/number-box";
import {ClipboardService, ETS_CLIPBOARD_LEGS_KEY} from "../../clipboard.service";
import {
    ComboHighlightedItem,
    ComboHighlightedUIMessage,
    MultiLegOrderDataUIMessage
} from "../../ui-messages/ui-messages";
import {PortfolioItemType} from "../../portfolios/portfolios.model";
import {MarketSide} from "../../trading-model/market-side.enum";
import {SessionService} from "../../authentication/session-service.service";
import {
    GenericConfirmationDialogComponent
} from "../../generic-confirmation-dialog/generic-confirmation-dialog.component";
import {ApgDataService} from "../../adjustment-pricing-grid/services/apg-data.service";
import {
    HedgesSortingDialogComponent,
    HedgesSortingDialogConfig
} from "./sorting-dialog/hedges-sorting-dialog.component";
import {OrdersChainDialogComponent} from "../../orders-chain-dialog/orders-chain-dialog.component";
import {OrdersChainDialogConfig} from "../../orders-chain-dialog/orders-chain-dialog.config";
import {CopyOrdersToService} from "../../adjustment-pricing-grid/services/copy-orders-to.service";
import {SettlementPriceService} from "../settlement-price.service";
import {HgNormalizationService} from "../services/hg-normalization.service";
import {DateTime} from 'luxon';
import {CallHedgePalette, PutHedgePalette} from "../../color-picker/hedge-palettes.const";
import {TradingInstrumentKind} from "../../trading-instruments/trading-instrument-kind.enum";
import {AccessControlService} from "../../access-control-service.class";
import {ApplyResult, CoPilotBookHedge, IHedgingGridCopilotFacade} from "../copilot/copilot-facade";
import {CoPilotPositionDto} from "../copilot/copilot-operations";
import {Leg, SolutionId} from "../copilot/copilot-domain";
import {CopilotProvenanceLedger} from "../copilot/copilot-provenance";
import {ExpirationChangeEvent} from "../../expiration-selector/expiration-selector.component";

type HgStateKey = 'hg.strikeWidth' | 'hg.strikeOffset' | 'hg.selectedStrategy'
    | 'hg.selectedOptionType' | 'hg.baseQty' | 'hg.autoSort';

function getHgStateKeys(): HgStateKey[] {
    return [
        'hg.strikeWidth',
        'hg.strikeOffset',
        'hg.selectedStrategy',
        'hg.selectedOptionType',
        'hg.baseQty',
        'hg.baseQty',
        'hg.autoSort'
    ];
}

export type LegsGroup = {
    side: 'Call' | 'Put';
    groupId: string;
    groupLabel: string,
    groupColor: string,
    groupOrder: number;
    underlying?: string;
    underlyingDisplayName?: string;
    legs: HedgePositionModel[],
    expirationsLinked: boolean;
    hcf?: number;
    hfcToDefaultQtyFactor?: number;
    selectedForMerge?: boolean;
    selectedForHarvest?: boolean;
};

// Snapshot of an OWNED leg's mutable overlay, captured before CoPilot takes it into its modification
// state, so un-Preview (FR-78/79/145) restores its EXACT pre-Preview state — including a value the
// USER had already set (the residual case). Captured by value; clone() is NOT used (it is lossy).
interface CopilotLegOverlayPhoto {
    transQty: number;
    removed: boolean;
    selectedForTrans: boolean;
    selectedForMerge: boolean;
    selectedForHarvest: boolean;
}

@Component({
    selector: 'ets-hg-settings-section',
    templateUrl: 'hg-settings-section.component.html',
    styleUrls: [
        './hg-settings-section.component.scss',
        '../hedging-grid-common-styles.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class HgSettingsSectionComponent implements OnInit, AfterViewInit, OnDestroy, IHedgingGridCopilotFacade {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _hedgePositionsService: HedgePositionsService,
        private readonly _tiService: TradingInstrumentsService,
        private readonly _optionsChainService: OptionsChainService,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _settingsStorageService: SettingsStorageService,
        private readonly _apgDataService: ApgDataService,
        private readonly _toastr: ToastrService,
        private readonly _messageBus: MessageBusService,
        private readonly _clipboardService: ClipboardService,
        private readonly _sessionService: SessionService,
        private readonly _copyOrdersToService: CopyOrdersToService,
        private readonly _settlementPriceService: SettlementPriceService,
        private readonly _normalizationService: HgNormalizationService,
        private readonly _accessControlService: AccessControlService
    ) {
    }

    private _unsubscriber = new Subject();
    private _selectedPortfolio: ApgPortfolio;
    private _baseUnderlying: string;
    private _callLegsByGroup: LegsGroup[] = undefined;
    private _putLegsByGroup: LegsGroup[] = undefined;
    private _baseState: { before: string[], after: string[] };
    private _initialPositions: HedgePosition[] = [];
    private _initialColorsAndLabels: Enumerable.IDictionary<string, { color: string; label: string }>;
    private _groupsStateIndex: { [ix: string]: boolean } = {};
    private _interval: number;

    private get initialPositions(): HedgePosition[] {
        return this._initialPositions;
    }

    private set initialPositions(value: HedgePosition[]) {
        this._initialPositions = value;

        this._initialColorsAndLabels = Enumerable.from(value)
            .select(x => `${x.groupId}|${x.label}|${x.color}`)
            .distinct()
            .select(x => {
                const parts = x.split('|');
                return {
                    groupId: parts[0],
                    label: parts[1],
                    color: parts[2]
                }
            })
            .toDictionary(
                x => x.groupId,
                x => ({label: x.label, color: x.color})
            );
    }

    @Output() reviewChangesClicked = new EventEmitter<boolean>();

    esHedgingEnabled = false;

    expandConfigurator: boolean = false;

    strategyList: Array<OptionStrategy> = [
        'BOS',
        'BOS-Mid',
        'Butterfly',
        'Condor',
        'Option - Long',
        'Option - Short',
        'Slingshot',
        'Slingshot - Modified',
        'Sponsored Long',
        'Vertical',
    ];

    private _isShortVersion = true;
    get isShortVersion(): boolean {
        return this._isShortVersion;
    }

    @DetectSetterChanges()
    set isShortVersion(value: boolean) {

        if (value && this.isMergeMode) {
            return;
        }

        this._isShortVersion = value;

        if (value) {
            setTimeout(() => {
                if (this.isMergeMode) {
                    this.cancelMergeOperation();
                }
            });
        }
    }

    selectedStrategy: OptionStrategy;

    selectedOptionType: 'Calls' | 'Puts' | 'Calls & Puts' = 'Calls';

    strikeWidth: number = 10;

    strikeOffsetMode: 'Offset' | 'Strike' = 'Offset';

    strikeOffset: number = 50;

    baseStrike: number;

    baseQty = 10;

    autoSort = true;

    priceToOpen = false;

    positions: HedgePositionModel[] = [];

    @DetectMethodChanges()
    restoreAcquirePositions(positions: HedgePositionModel[]): void {
        this.positions = positions;
    }

    tradingInstrument: TradingInstrument;

    get strikeLadderStep(): number {
        return getStrikeStep(this.tradingInstrument?.ticker);
    }

    selectedExpiration: OptionExpirationDescriptor;

    expirationList: OptionExpirationDescriptor[] = [];

    dxCenterTextStyleObj = {
        style: 'text-align: center'
    };

    @ViewChild(HgMergeDialogComponent)
    mergeDialogCmp: HgMergeDialogComponent;

    @ViewChild(GenericConfirmationDialogComponent)
    confirmationDialog: GenericConfirmationDialogComponent;

    @ViewChild(HedgesSortingDialogComponent)
    hedgesSortingDialog: HedgesSortingDialogComponent;

    @ViewChild(OrdersChainDialogComponent)
    ordersChainDialog: OrdersChainDialogComponent;

    isMergeMode = false;

    filterExpiration: OptionExpirationDescriptor;

    filterExpirationList: OptionExpirationDescriptor[];

    filterUnderlying: string | null = null;

    private get _underlyingTickerDisplayMap(): { ticker: string, displayName: string }[] {
        return Enumerable.from(this.positions)
            .select(x => x.underlying)
            .where(x => !isVoid(x))
            .distinct()
            .select(ticker => {
                const displayName = this._tiService.getInstrumentByTicker(ticker)?.displayName || ticker;
                return { ticker, displayName };
            })
            .toArray();
    }

    get filterUnderlyingList(): string[] {
        return this._underlyingTickerDisplayMap.map(x => x.ticker);
    }

    get underlyingDropdownItems(): string[] {
        const items = this._underlyingTickerDisplayMap.map(x => x.displayName);
        return ['All Hedges', ...items];
    }

    get underlyingDropdownValue(): string {
        if (!isVoid(this.filterUnderlying)) {
            const entry = this._underlyingTickerDisplayMap.find(x => x.ticker === this.filterUnderlying);
            return entry?.displayName || this.filterUnderlying;
        }
        return 'All Hedges';
    }

    get isUnderlyingFilterActive(): boolean {
        return !isVoid(this.filterUnderlying) && this.filterUnderlyingList.length > 1;
    }

    get isUnderlyingFilterReadOnly(): boolean {
        return this.filterUnderlyingList.length <= 1;
    }

    @Input() portfolioDefaultQty?: number;

    getSign = Math.sign;

    get hasCalls(): boolean {
        return this.positions.some(x => x.type === 'Call');
    }

    get hasPuts(): boolean {
        return this.positions.some(x => x.type === 'Put');
    }

    get optionTypeList(): any[] {
        if (isVoid(this.selectedStrategy)) {
            return [];
        }

        const v: any[] = ['Calls', 'Puts'];

        switch (this.selectedStrategy) {

            case "Butterfly - Double":
            case 'Collar':
            case 'Ladder':
            case 'Risk Reversal':
            case 'Iron Butterfly':
            case 'Iron Condor':
            case 'Strangle':
            case 'Straddle':
                v.push('Calls & Puts');
                break;
        }

        return v;
    }

    get isMergeDialogVisible(): boolean {
        return this.mergeDialogCmp?.visible;
    }

    get usedColors(): string[] {
        const used = this.getAllGroups().map(x => x.groupColor)
            .filter((val, ix, arr) => arr.indexOf(val) == ix);
        return used;
    }

    get usedCallColors(): string[] {
        const used = this.getLegGroups('Call').map(x => x.groupColor)
            .filter((val, ix, arr) => arr.indexOf(val) == ix);
        return used;
    }

    get usedPutColors(): string[] {
        const used = this.getLegGroups('Put').map(x => x.groupColor)
            .filter((val, ix, arr) => arr.indexOf(val) == ix);
        return used;
    }

    get canShowCustomSortDialog() {
        const noChangesCall = this.getLegGroups('Call').every(x => !this.doesGroupHaveAnyChanges(x));
        const noChangesPut = this.getLegGroups('Put').every(x => !this.doesGroupHaveAnyChanges(x));
        return noChangesCall && noChangesPut;
    }

    get canShowHedgesExpirationFilterDialog() {
        return true; // TODO
    }

    ngOnInit() {
        this._messageBus.of<{ enabled: boolean }>('Hg.EsHedgingModeChanged')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(msg => {
                this.esHedgingEnabled = msg.payload.enabled;
                this._changeDetector.detectChanges();
            });

        this._messageBus.of<{ side: 'Calls' | 'Puts', portfolio: ApgPortfolio }>('Hg.AfterStateApplied')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this._selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
                takeUntil(this._unsubscriber)
            )
            .subscribe(x => this.onAfterStateApplied(x.payload));

        this._messageBus.of('Hg.RefreshQuotes')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(() => this.onChange());

        this._messageBus.of<{ positions: HedgePosition[], portfolio: ApgPortfolio }>('Hg.InitialPositionsChanged')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this._selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
                takeUntil(this._unsubscriber)
            )
            .subscribe((msg) => {
                this.onInitialPositionsChanged(msg.payload);
            });

        this._messageBus.of<{ portfolio: ApgPortfolio }>('Hg.AfterReviewChanges')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this._selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
                takeUntil(this._unsubscriber)
            )
            .subscribe(() => this.afterReviewChanges());

        this._messageBus.of<{ portfolio: ApgPortfolio }>('Hg.HedgesCleared')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this._selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
                takeUntil(this._unsubscriber)
            )
            .subscribe(msg => this.onHedgesCleared(msg.payload));

        this._messageBus.of('ClipboardUpdated')
            .pipe(
                takeUntil(this._unsubscriber)
            )
            .subscribe(() => this._changeDetector.detectChanges());

        this._messageBus.of<any>('Hg.HedgeRemoved')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this._selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
                takeUntil(this._unsubscriber)
            )
            .subscribe((x) => this.onHedgeRemoved(x.payload));

        this._messageBus.of<any>('Hg.DefaultHedgesLoaded')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this._selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
                takeUntil(this._unsubscriber)
            )
            .subscribe((x) => this.onDefaultHedgesLoaded(x.payload));

        this._messageBus.of<{ ticker?: string }>('Qg.ChainsChangingMessage')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this._changeDetector.detectChanges());
        this._messageBus.of<{ ticker?: string }>('Qg.ChainsUpdatedMessage')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(message => {
                this.refreshWorkingOptionSeries(message.payload?.ticker);
                this._changeDetector.detectChanges();
            });

        this.runExpirationChecker();
    }

    private refreshWorkingOptionSeries(ticker?: string): void {
        for (const leg of this.positions) {
            // Use the current working selection, not the original held contract:
            // a manually changed or cleared expiration must survive a replay jump.
            const selected = leg.expirationDescriptor;
            const underlying = selected?.underlyingTicker || leg.underlying;
            if (!underlying || (ticker && underlying !== ticker)
                || !this._optionsChainService.isChainConfirmed(underlying)) {
                continue;
            }
            const chain = this._optionsChainService.getCachedChain(underlying);
            if (!chain) {
                continue;
            }
            leg.expirationList = chain.expirations.slice();
            if (!selected) {
                continue;
            }
            const expiration = chain.expirations.find(x =>
                x.underlyingTicker === selected.underlyingTicker
                && x.expirationTicker === selected.expirationTicker
                && x.optionExpirationDate === selected.optionExpirationDate);
            if (!expiration) {
                continue;
            }
            leg.expirationDescriptor = expiration;
            if (expiration.visible) {
                leg.expiredTicker = undefined;
            }
        }
        this.refreshFilterExpirations();
    }

    isLegSeriesReady(leg: HedgePositionModel): boolean {
        const selected = leg.expirationDescriptor;
        const underlying = selected?.underlyingTicker || leg.underlying;
        if (!underlying || !this._optionsChainService.isChainConfirmed(underlying)) {
            return false;
        }
        // A deliberately cleared expiration remains editable. Otherwise only a
        // descriptor from the confirmed current chain may initialize the editors.
        const chain = this._optionsChainService.getCachedChain(underlying);
        return !!chain && (!selected || (!!selected.visible && chain.expirations.includes(selected)
            && (isVoid(leg.strike) || selected.strikes?.includes(leg.strike))));
    }

    getLegSeriesDateText(leg: HedgePositionModel): string {
        const day = leg.expirationDescriptor?.optionExpirationDate;
        return day ? makeGuiFriendlyExpirationDate(day) : '';
    }

    private onDefaultHedgesLoaded(payload: any): void {
        this.onReloadHedgesClicked();
    }

    @DetectMethodChanges()
    private onHedgeRemoved(payload: { hedgeId: string, side: 'Call' | 'Put' }) {
        this.positions = this.positions.filter(x => x.groupId !== payload.hedgeId);
        this.afterReviewChanges();
        this.refreshLegsSuppressingDivergence(payload.side);   // M3-6: cross-component sync (Positions delete) keeps CoPilot refs — NOT a manual edit
    }

    @DetectMethodChanges()
    private onInitialPositionsChanged(payload: { positions: HedgePosition[] }) {
        this.initialPositions = payload.positions;
        this.isMergeMode = false;
    }

    ngAfterViewInit() {
        this.hedgesSortingDialog.hedgesReordered$.subscribe(x => this.onCustomSortChanged());
    }

    ngOnDestroy() {
        this._unsubscriber.next();
        this._unsubscriber.complete();
        if (!isVoid(this._interval)) {
            clearInterval(this._interval);
        }
    }

    getLegGroups(side: 'Call' | 'Put'): LegsGroup[] {

        if (side !== 'Call' && side !== 'Put') {
            return;
        }

        let groupField = side === 'Call' ? this._callLegsByGroup : this._putLegsByGroup;

        if (!isVoid(groupField)) {
            return groupField;
        }

        let grpsEnum = Enumerable
            .from(this.positions)
            .where(x => x.type === side)
            .groupBy(x => x.groupId + x.label)
            .select(x => {

                let legs = x.toArray();

                if (this.autoSort) {
                    legs = Enumerable.from(legs)
                        .orderByDescending(x => x.strike)
                        .toArray();
                }

                const groupId = legs[0].groupId;

                const label = legs[0].label;

                const groupOrder = legs[0].groupOrder;

                let linkedState = this._groupsStateIndex[groupId];

                if (isNullOrUndefined(linkedState)) {
                    linkedState = true;
                }

                const ulTicker = legs[0].underlying;
                const ulDisplayName = this._tiService.getInstrumentByTicker(ulTicker)?.displayName || ulTicker;

                return {
                    side: side,
                    groupId: groupId,
                    groupLabel: label,
                    groupColor: legs[0].color,
                    groupOrder: groupOrder,
                    underlying: ulTicker,
                    underlyingDisplayName: ulDisplayName,
                    legs: legs,
                    expirationsLinked: linkedState
                } as LegsGroup;
            });

        if (!isVoid(this.filterExpiration)) {
            grpsEnum = grpsEnum
                .where(x =>
                    x.legs.some(leg => leg.expiration === this.filterExpiration.optionExpirationDate));
        }

        if (!isVoid(this.filterUnderlying)) {
            grpsEnum = grpsEnum.where(x => x.underlying === this.filterUnderlying);
        }

        if (this.autoSort) {
            grpsEnum = grpsEnum.orderByDescending(grp => grp.legs[grp.legs.length - 1].strike)
                .thenBy(grp => grp.groupLabel || '')
        } else {
            grpsEnum = grpsEnum.orderBy(grp => grp.groupOrder);
        }

        const grps = grpsEnum.toArray();

        grps.forEach(grp => grp.legs.forEach(leg => {
            const lastQuote = this._lastQuoteCache.getLastQuote(leg.getTicker());
            leg.price = lastQuote?.mid;
        }));

        if (side === 'Call') {
            this._callLegsByGroup = groupField = grps;
        } else {
            this._putLegsByGroup = groupField = grps;
        }

        return groupField;

    }

    @DetectMethodChanges()
    onChange() {
    }

    @DetectMethodChanges({isAsync: true})
    async onPortfolioSelected(args: ApgPortfolio) {

        this._settlementPriceService.reset();

        this._selectedPortfolio = args;
        this._baseUnderlying = await this._apgDataService.getUnderlyingOfPortfolio(args);

        let positionModels: HedgePositionModel[] = [];

        try {
            positionModels = await this._hedgePositionsService.getPositionModels(args);
            positionModels.forEach(x => x.expirationPnl = undefined);
        } catch (e) {
            this._toastr.error('Hedge Positions Restored With Errors.\nProbably one or more contracts are expired');
        }

        this.initialPositions = positionModels.map(x => x.asDto());

        this.positions = positionModels.flatMap(x => x);

        if (!args?.isIndependent) {

            let portfolioPositions: BeforePositionDto[][] = [];
            try {
                portfolioPositions = await this._apgDataService.getPortfolioPositions(args);
            } catch (e) {
                console.error(e);
                this._toastr.error('Portfolio Positions Loaded With Errors.\nProbably one or more contracts are expired');
            }

            if (!isVoid(portfolioPositions)) {

                await this.isPortfolioValid();

            } else {
                this._toastr.error('Cannot Determine Portfolio Positions')
            }

            if (!isVoid(this.positions)) {

                const underlying = this.positions[0].underlying;

                if (!isVoid(underlying)) {
                    const ti = this._tiService.getInstrumentByTicker(underlying);
                    await this.onInstrumentSelected(ti);
                }

            } else {
                if (!isVoid(portfolioPositions)) {
                    const optionTicker = parseOptionTicker(portfolioPositions[0][0].ticker);
                    if (!isVoid(optionTicker)) {
                        if (!isVoid(optionTicker.underlying)) {
                            const ti = this._tiService.getInstrumentByTicker(optionTicker.underlying);
                            await this.onInstrumentSelected(ti);
                        }
                    }
                }
            }

        } else {

            // Independent HG portfolio (CR-20260606-001): no APG positions, no expiry gate.
            // Resolve the trading instrument from the portfolio's own underlying (set at
            // creation), or from existing hedge legs if any are loaded.
            let underlying = this._baseUnderlying;
            if (!isVoid(this.positions)) {
                underlying = this.positions[0].underlying || underlying;
            }
            if (!isVoid(underlying)) {
                const ti = this._tiService.getInstrumentByTicker(underlying);
                await this.onInstrumentSelected(ti);
            }
        }

        this.refreshLegs('Calls & Puts');

        this.updateQuoteSubscriptions(this.positions, true);

        this.restoreHgState();

        this.strikeWidth = snapToStrikeStep(this.strikeWidth, this.tradingInstrument?.ticker);
    }

    @DetectMethodChanges({isAsync: true})
    async removeLeg(leg: HedgePositionModel, side: 'Call' | 'Put') {

        if (this.isLegExpired(leg)) {
            try {
                await this.confirmationDialog.show([
                    "Are you sure you want to delete the leg?"
                ]);
            } catch {
                return;
            }
        }

        await this.runWithLoadingIndicator(() => {
            if (this.isOwnedLeg(leg) && !this.isLegExpired(leg)) {
                leg.removed = true;
                this.checkCopilotDivergence();   // M3-6: in-place soft-remove bypasses refreshLegs
            } else {
                this.positions = this.positions.filter(x => x !== leg);
                this.refreshLegs(side);
                if (this.isLegExpired(leg)) {
                    this._messageBus.publishAsync({
                        topic: 'Hg.RemoveExpiredLeg',
                        payload: {
                            leg,
                            portfolio: this._selectedPortfolio
                        }
                    });
                }
            }
        });
    }

    @DetectMethodChanges({isAsync: true})
    async restoreLeg(leg: HedgePositionModel) {
        await this.runWithLoadingIndicator(() => {
            leg.removed = false;
            this.checkCopilotDivergence();   // M3-6: in-place restore bypasses refreshLegs
        });
    }

    @DetectMethodChanges()
    onLabelChanged(group: LegsGroup, args: DxValueChanged<string>) {

        if (isVoid(args.value)) {
            this._toastr.error("Hedge's label cannot be empty string");
            return;
        }

        group.legs.forEach(l => l.label = args.value?.trim());
    }

    @DetectMethodChanges({isAsync: true})
    async onAddStrategyClicked() {

        if (isVoid(this.selectedOptionType)) {
            this._toastr.error('Please provide "Option Type"');
            return;
        }

        if (isVoid(this.selectedStrategy)) {
            this._toastr.error('Please provide "Option Strategy"');
            return;
        }

        if (!isValidNumber(this.strikeWidth)) {
            if (!this.selectedStrategy.startsWith('Option')) {
                this._toastr.error('Please provide "Strike Width"');
                return;
            }
        }

        if (!isValidNumber(this.baseQty, true)) {
            this._toastr.error('Please provide valid "Base Qty"');
            return;
        }

        if (this._baseUnderlying && !this.isAllowedUnderlying(this.tradingInstrument.ticker, this._baseUnderlying)) {
            this._toastr.error('Cannot Add Strategy Because Underlying Assets Do Not Match');
            return;
        }

        await this.runWithLoadingIndicator(async () => {
            const models = await this.setupPricingModel();

            let autoColor = undefined;

            if (this.selectedOptionType === 'Calls') {
                autoColor = Enumerable.from(CallHedgePalette).except(this.usedCallColors)
                    .firstOrDefault();
            } else if (this.selectedOptionType === 'Puts') {
                autoColor = Enumerable.from(PutHedgePalette).except(this.usedPutColors)
                    .firstOrDefault();
            }

            models.forEach(m => m.color = autoColor);

            this.positions.push(...models);

            this.refreshLegs(this.selectedOptionType);

            this.updateQuoteSubscriptions(models, true);
        });
    }

    private refreshLegs(side: 'Calls' | 'Call' | 'Puts' | 'Put' | 'Calls & Puts' | 'Both') {

        this.refreshFilterExpirations();

        if (isVoid(side) || side === 'Calls & Puts' || side === 'Both') {
            this._callLegsByGroup = undefined;
            this._putLegsByGroup = undefined;
        } else {
            if (side.startsWith('C')) {
                this._callLegsByGroup = undefined;
            } else if (side.startsWith('P')) {
                this._putLegsByGroup = undefined;
            }
        }

        // M3-6: every leg add/remove/resize repaints through here — detect a user manual edit on top of
        // CoPilot-suggested legs and warn once (no-op for CoPilot's own repaint / when no CoPilot legs).
        this.checkCopilotDivergence();
    }

    async setupPricingModel(): Promise<HedgePositionModel[]> {

        const strategy = this.selectedStrategy;

        if (isNullOrUndefined(strategy)) {
            return Promise.resolve(null);
        }

        const underlying = this.tradingInstrument.ticker;

        const chain = await this._optionsChainService.getChain(underlying);

        const nearestExp = this.selectedExpiration;

        const lastQuote = await this._lastQuoteCache
            .getLastQuoteWithAwait(underlying);

        const centerIx = findAtmStrikeIndex(nearestExp.strikes, lastQuote);

        const legs: HedgePositionModel[] = [];

        const expirationList = chain.expirations;

        const qtyMultiplier = this._normalizationService.getQtyMultiplier(
            this.tradingInstrument.ticker, this._baseUnderlying
        );
        const baseQty = Math.round((this.baseQty || 1) * qtyMultiplier);

        const offset = this.strikeOffsetMode === 'Offset' ? (this.strikeOffset || 0) : 0;

        const atmStrike = this.strikeOffsetMode === 'Offset'
            ? nearestExp.strikes[centerIx] : this.baseStrike;

        function pickStrike(distance: number) {

            if (distance === 0) {
                return atmStrike;
            }

            let targetStrike = atmStrike + distance;

            return targetStrike;
        }


        if (strategy === 'BOS') {
            const width = this.strikeWidth;

            const offset = this.strikeOffset || 0;

            const oType = this.selectedOptionType !== 'Puts' ? 'Call' : 'Put';

            const multiplier = this.selectedOptionType === 'Calls' ? 1 : -1;

            const firstStrike = atmStrike + offset * multiplier;

            const label = oType + ' BOS';

            const groupId = getShortUUID();

            const step = this.strikeLadderStep;

            const leg1 = new HedgePositionModel();
            leg1.underlying = this.tradingInstrument.ticker;
            leg1.side = 'Buy';
            leg1.qty = 1 * baseQty;
            leg1.expirationDescriptor = nearestExp;
            leg1.expirationList = expirationList;
            leg1.strike = firstStrike;
            leg1.type = oType;
            leg1.label = label;
            leg1.groupId = groupId;
            leg1.strategy = strategy;

            const leg2 = new HedgePositionModel();
            leg2.underlying = this.tradingInstrument.ticker;
            leg2.side = 'Sell';
            leg2.qty = -1 * baseQty;
            leg2.expirationDescriptor = nearestExp;
            leg2.expirationList = expirationList;
            leg2.strike = leg1.strike + width * multiplier;
            leg2.type = oType;
            leg2.label = label;
            leg2.groupId = groupId;
            leg2.strategy = strategy;


            const creditSpreadQty = width / step;

            const leg3 = new HedgePositionModel();
            leg3.underlying = this.tradingInstrument.ticker;
            leg3.side = 'Sell';
            leg3.qty = -creditSpreadQty * baseQty;
            leg3.expirationDescriptor = nearestExp;
            leg3.expirationList = expirationList;
            leg3.strike = leg2.strike + step * multiplier;
            leg3.type = oType;
            leg3.label = label;
            leg3.groupId = groupId;
            leg3.strategy = strategy;

            const leg4 = new HedgePositionModel();
            leg4.underlying = this.tradingInstrument.ticker;
            leg4.side = 'Buy';
            leg4.qty = creditSpreadQty * baseQty;
            leg4.expirationDescriptor = nearestExp;
            leg4.expirationList = expirationList;
            leg4.strike = leg3.strike + step * multiplier;
            leg4.type = oType;
            leg4.label = label;
            leg4.groupId = groupId;
            leg4.strategy = strategy;

            legs.push(leg1, leg2, leg3, leg4);

            if (this.selectedOptionType === 'Calls') {
                legs.reverse();
            }

        } else if (strategy === 'BOS-Mid') {
            const width = this.strikeWidth;

            const offset = this.strikeOffset || 0;

            const oType = this.selectedOptionType !== 'Puts' ? 'Call' : 'Put';

            const multiplier = this.selectedOptionType === 'Calls' ? 1 : -1;

            const firstStrike = atmStrike + offset * multiplier;

            const label = oType + ' BOS-Mid';

            const groupId = getShortUUID();

            const leg1 = new HedgePositionModel();
            leg1.underlying = this.tradingInstrument.ticker;
            leg1.side = 'Buy';
            leg1.qty = 1 * baseQty;
            leg1.expirationDescriptor = nearestExp;
            leg1.expirationList = expirationList;
            leg1.strike = firstStrike;
            leg1.type = oType;
            leg1.label = label;
            leg1.groupId = groupId;
            leg1.strategy = strategy;

            const leg2 = new HedgePositionModel();
            leg2.underlying = this.tradingInstrument.ticker;
            leg2.side = 'Sell';
            leg2.qty = -3 * baseQty;
            leg2.expirationDescriptor = nearestExp;
            leg2.expirationList = expirationList;
            leg2.strike = leg1.strike + width * 2 * multiplier;
            leg2.type = oType;
            leg2.label = label;
            leg2.groupId = groupId;
            leg2.strategy = strategy;

            const leg3 = new HedgePositionModel();
            leg3.underlying = this.tradingInstrument.ticker;
            leg3.side = 'Buy';
            leg3.qty = 3 * baseQty;
            leg3.expirationDescriptor = nearestExp;
            leg3.expirationList = expirationList;
            leg3.strike = leg2.strike + width * multiplier;
            leg3.type = oType;
            leg3.label = label;
            leg3.groupId = groupId;
            leg3.strategy = strategy;

            const leg4 = new HedgePositionModel();
            leg4.underlying = this.tradingInstrument.ticker;
            leg4.side = 'Sell';
            leg4.qty = -1 * baseQty;
            leg4.expirationDescriptor = nearestExp;
            leg4.expirationList = expirationList;
            leg4.strike = leg3.strike + width * 2 * multiplier;
            leg4.type = oType;
            leg4.label = label;
            leg4.groupId = groupId;
            leg4.strategy = strategy;

            legs.push(leg1, leg2, leg3, leg4);

            if (this.selectedOptionType === 'Calls') {
                legs.reverse();
            }

        } else if (strategy === 'Butterfly') {

            const optionType = this.selectedOptionType !== 'Puts'
                ? 'Call'
                : 'Put';

            const multiplier = optionType === 'Put' ? -1 : 1;

            const groupId = getShortUUID();

            const label = optionType + ' Butterfly';

            const leg1 = new HedgePositionModel();
            leg1.underlying = this.tradingInstrument.ticker
            leg1.qty = 1 * baseQty;
            leg1.side = 'Buy';
            leg1.expirationDescriptor = nearestExp;
            leg1.expirationList = expirationList;
            leg1.strike = atmStrike + offset * multiplier;
            leg1.type = optionType;
            leg1.label = label
            leg1.groupId = groupId;

            const leg2 = new HedgePositionModel();
            leg2.underlying = this.tradingInstrument.ticker
            leg2.qty = -2 * baseQty;
            leg2.side = 'Sell';
            leg2.expirationDescriptor = nearestExp;
            leg2.expirationList = expirationList;
            leg2.strike = leg1.strike + this.strikeWidth * multiplier;
            leg2.type = optionType;
            leg2.label = label;
            leg2.groupId = groupId;

            const leg3 = new HedgePositionModel();
            leg3.underlying = this.tradingInstrument.ticker
            leg3.side = 'Buy';
            leg3.qty = 1 * baseQty;
            leg3.expirationDescriptor = nearestExp;
            leg3.expirationList = expirationList;
            leg3.strike = leg2.strike + this.strikeWidth * multiplier;
            leg3.type = optionType;
            leg3.label = label;
            leg3.groupId = groupId;

            legs.push(leg1, leg2, leg3);

            if (optionType === 'Call') {
                legs.reverse();
            }
        } else if (strategy === 'Condor') {

            const offset = this.strikeOffset || 0;
            const width = this.strikeWidth;

            const oType = this.selectedOptionType !== 'Puts' ? 'Call' : 'Put';

            const multiplier = this.selectedOptionType === 'Calls' ? 1 : -1;

            const firstStrike = atmStrike + offset * multiplier;


            const label = oType + ' Condor';
            const groupId = getShortUUID();


            const leg1 = new HedgePositionModel();
            leg1.underlying = this.tradingInstrument.ticker;
            leg1.side = 'Buy';
            leg1.qty = 1 * baseQty;
            leg1.expirationDescriptor = nearestExp;
            leg1.expirationList = expirationList;
            leg1.strike = firstStrike;
            leg1.type = oType;
            leg1.label = label;
            leg1.groupId = groupId;


            const leg2 = new HedgePositionModel();
            leg2.underlying = this.tradingInstrument.ticker;
            leg2.side = 'Sell';
            leg2.qty = -1 * baseQty;
            leg2.expirationDescriptor = nearestExp;
            leg2.expirationList = expirationList;
            leg2.strike = leg1.strike + width * multiplier;
            leg2.type = oType;
            leg2.label = label;
            leg2.groupId = groupId;

            const leg3 = new HedgePositionModel();
            leg3.underlying = this.tradingInstrument.ticker;
            leg3.side = 'Sell';
            leg3.qty = -1 * baseQty;
            leg3.expirationDescriptor = nearestExp;
            leg3.expirationList = expirationList;
            leg3.strike = leg2.strike + width * 2 * multiplier;
            leg3.type = oType;
            leg3.label = label;
            leg3.groupId = groupId;

            const leg4 = new HedgePositionModel();
            leg4.underlying = this.tradingInstrument.ticker;
            leg4.side = 'Buy';
            leg4.qty = 1 * baseQty;
            leg4.expirationDescriptor = nearestExp;
            leg4.expirationList = expirationList;
            leg4.strike = leg3.strike + width * multiplier;
            leg4.type = oType;
            leg4.label = label;
            leg4.groupId = groupId;

            legs.push(leg1, leg2, leg3, leg4);

            if (this.selectedOptionType === 'Calls') {
                legs.reverse();
            }

        } else if (strategy === 'Option - Long') {

            const strike = this.selectedOptionType === 'Calls'
                ? pickStrike(offset || 0)
                : pickStrike(-offset || 0)

            const oType = this.selectedOptionType !== 'Puts' ? 'Call' : 'Put';

            const leg1 = new HedgePositionModel();
            leg1.underlying = this.tradingInstrument.ticker;
            leg1.side = 'Buy';
            leg1.qty = 1 * baseQty;
            leg1.expirationDescriptor = nearestExp;
            leg1.expirationList = expirationList;
            leg1.strike = strike;
            leg1.type = oType;
            leg1.label = 'Long ' + oType;
            leg1.groupId = getShortUUID();

            legs.push(leg1);

        } else if (strategy === 'Option - Short') {
            const optionType = this.selectedOptionType !== 'Puts'
                ? 'Call'
                : 'Put';
            const multiplier = optionType === 'Put' ? -1 : 1;
            const oType = this.selectedOptionType !== 'Puts' ? 'Call' : 'Put';
            const strike = atmStrike + offset * multiplier

            const leg1 = new HedgePositionModel();
            leg1.underlying = this.tradingInstrument.ticker;
            leg1.side = 'Sell';
            leg1.qty = -1 * baseQty;
            leg1.expirationDescriptor = nearestExp;
            leg1.expirationList = expirationList;
            leg1.strike = strike;
            leg1.type = oType;
            leg1.label = 'Short ' + oType;
            leg1.groupId = getShortUUID();

            legs.push(leg1);

        } else if (strategy === 'Vertical') {

            const optionType = this.selectedOptionType !== 'Puts' ? 'Call' : 'Put';
            const multiplier = this.selectedOptionType === 'Puts' ? -1 : 1;

            const label = this.selectedStrategy;

            const groupId = getShortUUID();

            const leg1 = new HedgePositionModel();
            leg1.underlying = this.tradingInstrument.ticker
            leg1.qty = 1 * baseQty;
            leg1.side = 'Buy';
            leg1.expirationDescriptor = nearestExp;
            leg1.expirationList = expirationList;
            leg1.strike = atmStrike + offset * multiplier;
            leg1.type = optionType;
            leg1.label = label;
            leg1.groupId = groupId;

            const leg2 = new HedgePositionModel();
            leg2.underlying = this.tradingInstrument.ticker;
            leg2.side = 'Sell';
            leg2.qty = -1 * baseQty;
            leg2.expirationDescriptor = nearestExp;
            leg2.expirationList = expirationList;
            leg2.strike = leg1.strike + this.strikeWidth * multiplier;
            leg2.type = optionType;
            leg2.label = label;
            leg2.groupId = groupId;

            legs.push(leg2, leg1);

            if (optionType === 'Put') {
                legs.reverse();
            }
        } else if (strategy === 'Slingshot') {

            const oType = this.selectedOptionType !== 'Puts'
                ? 'Call'
                : 'Put';

            const multiplier = oType === 'Put' ? -1 : 1;

            const label = oType === 'Call' ? 'Call Slingshot' : 'Put Slingshot';
            const groupId = getShortUUID();

            const leg1 = new HedgePositionModel();
            leg1.underlying = this.tradingInstrument.ticker
            leg1.qty = 1 * baseQty;
            leg1.side = 'Buy';
            leg1.expirationDescriptor = nearestExp;
            leg1.expirationList = expirationList;
            leg1.strike = atmStrike + offset * multiplier;
            leg1.type = oType;
            leg1.label = label;
            leg1.groupId = groupId;

            const leg2 = new HedgePositionModel();
            leg2.underlying = this.tradingInstrument.ticker
            leg2.qty = -2 * baseQty;
            leg2.side = 'Sell';
            leg2.expirationDescriptor = nearestExp;
            leg2.expirationList = expirationList;
            leg2.strike = leg1.strike + this.strikeWidth * multiplier;
            leg2.type = oType;
            leg2.label = label;
            leg2.groupId = groupId;

            const leg3 = new HedgePositionModel();
            leg3.underlying = this.tradingInstrument.ticker
            leg3.qty = 2 * baseQty;
            leg3.side = 'Buy';
            leg3.expirationDescriptor = nearestExp;
            leg3.expirationList = expirationList;
            leg3.strike = leg2.strike + this.strikeWidth * multiplier;
            leg3.type = oType;
            leg3.label = label;
            leg3.groupId = groupId;

            legs.push(leg1, leg2, leg3);

            if (oType === 'Call') {
                legs.reverse();
            }
        } else if (strategy === 'Slingshot - Modified') {

            const oType = this.selectedOptionType !== 'Puts'
                ? 'Call'
                : 'Put';

            const multiplier = oType === 'Put' ? -1 : 1;

            const label = oType === 'Call' ? 'C. Slingshot - Modified' : 'P. Slingshot - Modified';
            const groupId = getShortUUID();

            const leg1 = new HedgePositionModel();
            leg1.underlying = this.tradingInstrument.ticker
            leg1.qty = 1 * baseQty;
            leg1.side = 'Buy';
            leg1.expirationDescriptor = nearestExp;
            leg1.expirationList = expirationList;
            leg1.strike = atmStrike + offset * multiplier;
            leg1.type = oType;
            leg1.label = label;
            leg1.groupId = groupId;

            const leg2 = new HedgePositionModel();
            leg2.underlying = this.tradingInstrument.ticker
            leg2.qty = -2 * baseQty;
            leg2.side = 'Sell';
            leg2.expirationDescriptor = nearestExp;
            leg2.expirationList = expirationList;
            leg2.strike = leg1.strike + this.strikeWidth * multiplier;
            leg2.type = oType;
            leg2.label = label;
            leg2.groupId = groupId;

            const leg3 = new HedgePositionModel();
            leg3.underlying = this.tradingInstrument.ticker
            leg3.qty = 2 * baseQty;
            leg3.side = 'Buy';
            leg3.expirationDescriptor = nearestExp;
            leg3.expirationList = expirationList;
            leg3.strike = leg2.strike + this.strikeWidth * multiplier;
            leg3.type = oType;
            leg3.label = label;
            leg3.groupId = groupId;

            const leg4 = new HedgePositionModel();
            leg4.underlying = this.tradingInstrument.ticker
            leg4.qty = -1 * baseQty;
            leg4.side = 'Sell';
            leg4.expirationDescriptor = nearestExp;
            leg4.expirationList = expirationList;
            leg4.strike = leg3.strike + this.strikeWidth * multiplier;
            leg4.type = oType;
            leg4.label = label;
            leg4.groupId = groupId;

            legs.push(leg1, leg2, leg3, leg4);

            if (oType === 'Call') {
                legs.reverse();
            }
        } else if (strategy === 'Sponsored Long') {
            const width = this.strikeWidth;

            const offset = this.strikeOffset || 0;

            const oType = this.selectedOptionType !== 'Puts' ? 'Call' : 'Put';

            const multiplier = this.selectedOptionType === 'Calls' ? 1 : -1;

            const firstStrike = atmStrike + offset * multiplier;

            const label = oType + ' Sponsored Long';

            const groupId = getShortUUID();

            const step = this.strikeLadderStep;

            const leg1 = new HedgePositionModel();
            leg1.underlying = this.tradingInstrument.ticker;
            leg1.side = 'Buy';
            leg1.qty = 1 * baseQty;
            leg1.expirationDescriptor = nearestExp;
            leg1.expirationList = expirationList;
            leg1.strike = firstStrike;
            leg1.type = oType;
            leg1.label = label;
            leg1.groupId = groupId;
            leg1.strategy = strategy;

            const creditSpreadQty = width / step;

            const leg2 = new HedgePositionModel();
            leg2.underlying = this.tradingInstrument.ticker;
            leg2.side = 'Sell';
            leg2.qty = -creditSpreadQty * baseQty;
            leg2.expirationDescriptor = nearestExp;
            leg2.expirationList = expirationList;
            leg2.strike = leg1.strike + width * multiplier;
            leg2.type = oType;
            leg2.label = label;
            leg2.groupId = groupId;
            leg2.strategy = strategy;

            const leg3 = new HedgePositionModel();
            leg3.underlying = this.tradingInstrument.ticker;
            leg3.side = 'Buy';
            leg3.qty = creditSpreadQty * baseQty;
            leg3.expirationDescriptor = nearestExp;
            leg3.expirationList = expirationList;
            leg3.strike = leg2.strike + step * multiplier;
            leg3.type = oType;
            leg3.label = label;
            leg3.groupId = groupId;
            leg3.strategy = strategy;

            legs.push(leg1, leg2, leg3);

            if (this.selectedOptionType === 'Calls') {
                legs.reverse();
            }

        } else {
            console.error('Unknown strategy');
            return undefined;
        }

        if (!this.autoSort) {
            legs.forEach(x => x.groupOrder = this.selectedOptionType === 'Calls' ? 1000 : -1);
        }

        return legs;
    }

    @DetectMethodChanges({isAsync: true})
    async onAddOptionClicked(type: 'Call' | 'Put') {
        if (this._baseUnderlying && !this.isAllowedUnderlying(this.tradingInstrument.ticker, this._baseUnderlying)) {
            this._toastr.error('Cannot Add Strategy Because Underlying Assets Do Not Match');
            return;
        }

        await this.runWithLoadingIndicator(async () => {
            const chain = await this._optionsChainService.getChain(this.tradingInstrument.ticker);

            const qtyMultiplier = this._normalizationService.getQtyMultiplier(
                this.tradingInstrument.ticker, this._baseUnderlying
            );

            const model = new HedgePositionModel();
            model.qty = Math.round(this.baseQty * qtyMultiplier);
            model.underlying = this.tradingInstrument.ticker;
            model.groupId = getShortUUID();
            model.expirationList = chain.expirations;
            model.expirationDescriptor = this.selectedExpiration;
            model.type = type;

            this.positions.push(model);

            this.refreshLegs(type);
        });
    }

    @DetectMethodChanges({isAsync: true})
    async onClearLegsClicked(silent?: boolean) {

        if (this.positions.length > 0) {
            if (!silent) {
                try {
                    await this.confirmationDialog?.show(['Are you sure you want to clear current legs?']);
                } catch {
                    return;
                }
            }
        }

        await this.runWithLoadingIndicator(() => {
            const positions = this.positions.slice()
            this.positions = [];
            this.isMergeMode = false;
            this.refreshLegs('Calls & Puts');
            this.updateQuoteSubscriptions(positions, false, true);
        });
    }

    @DetectMethodChanges({isAsync: true})
    async onReloadHedgesClicked() {

        if (this.positions.length > 0) {
            try {
                await this.confirmationDialog?.show(['Are you sure you want to clear current legs and reload initial hedges?']);
            } catch {
                return;
            }
        }

        await this.runWithLoadingIndicator(async () => {
            this._messageBus.publish({
                topic: 'Hg.HedgesCleared',
                payload: {
                    isReload: true,
                    portfolio: this._selectedPortfolio
                }
            });


            const positions = this.positions.slice();

            this.updateQuoteSubscriptions(positions, false, true);

            this.positions = [];

            this._groupsStateIndex = {};

            this._initialColorsAndLabels = undefined;

            this.isMergeMode = false;

            if (!isVoid(this._selectedPortfolio)) {
                const positionModels =
                    await this._hedgePositionsService.getPositionModels(this._selectedPortfolio);

                this.positions = positionModels.flatMap(x => x);

                this.updateQuoteSubscriptions(this.positions, true);

                this.afterReviewChanges();
            }

            this.refreshLegs('Calls & Puts');
        });

    }

    @DetectMethodChanges()
    onLegQtyChange(leg: HedgePositionModel, qty: number) {
        this.checkCopilotDivergence();   // M3-6: in-place qty edit bypasses refreshLegs — detect divergence here
        if (isVoid(leg.side)) {
            leg.qty = qty;
            return;
        }
        const q = Math.abs(qty);
        const sign = leg.side === 'Buy' ? 1 : -1;
        leg.qty = q * sign;
    }

    onLegSideChanging(leg: HedgePositionModel, $event: any) {
        if (leg.side === $event) {
            return;
        }

        this.cloneAndRemoveLeg(leg, 'side');
    }

    private cloneAndRemoveLeg(leg: HedgePositionModel, prop: keyof HedgePositionModel) {

        if (isVoid(leg)) {
            return;
        }

        if (!this.isOwnedLeg(leg)) {
            return;
        }

        if (isVoid(leg[prop])) {
            return;
        }

        const clone = leg.clone();

        clone.resetTransState();

        clone.removed = true;

        const ix = this.positions.indexOf(leg);

        if (ix < 0) {
            return;
        }

        this.positions.splice(ix, 0, clone);

        this.refreshLegs(leg.type);

    }

    @DetectMethodChanges()
    onLegSideChanged(leg: HedgePositionModel) {

        if (!isValidNumber(leg.qty)) {
            return;
        }

        if (leg.side === 'Sell') {
            leg.qty *= -1;
        } else if (leg.side === 'Buy') {
            leg.qty = Math.abs(leg.qty);
        }

        this.checkCopilotDivergence();   // M3-6: side flip on a CoPilot-ADDED (non-owned) leg bypasses refreshLegs
    }

    @DetectMethodChanges()
    onHedgeColorChanged(args: DxValueChanged<string>, pGroup: LegsGroup) {
        pGroup.legs.forEach(x => x.color = args.value);
    }

    @DetectMethodChanges({isAsync: true})
    async onSymbolPickerInstrumentSelected($event: TradingInstrument) {
        await this.onInstrumentSelected($event);
    }

    private async onInstrumentSelected($event: TradingInstrument) {
        this.tradingInstrument = $event;
        const chain = await this._optionsChainService.getChain($event.ticker);
        this.expirationList = chain.expirations;
        this.selectedExpiration = await this.getShortOptionExpiration();
        // (The CoPilot strike-context refresh is no longer driven from here — the Hedge Pad is out of the
        // CoPilot path. The matrix now owns + announces that context via Hg.HedgeMatrixStrikeContextChanged.)
    }

    private saveHgState(stateKey: HgStateKey, value?: any) {
        if (isVoid(stateKey)) {
            return;
        }

        const attr = stateKey.split('.')[1];

        value = value ?? this[attr];

        const userId = this._sessionService.sessionData.userId;

        this._settingsStorageService.setItem(stateKey, value, userId);
    }

    @DetectMethodChanges()
    onStrategyChanged() {
        this.saveHgState('hg.selectedStrategy');
    }

    @DetectMethodChanges()
    onOptionTypeChanged() {
        this.saveHgState('hg.selectedOptionType');
    }

    @DetectMethodChanges()
    onStrikeWidthChanged() {
        this.saveHgState('hg.strikeWidth');
    }

    @DetectMethodChanges()
    onOffsetChanged() {
        this.saveHgState('hg.strikeOffset');
    }

    @DetectMethodChanges()
    onBaseQtyChanged() {
        this.saveHgState('hg.baseQty');
    }

    @DetectMethodChanges()
    onCustomSortChanged() {
        this.autoSort = false;
        this.publishLoading(true);
        setTimeout(() => {
            try {
                this.afterSortOrderChanged();
            } finally {
                this.publishLoading(false);
            }
        }, 250);
    }

    private afterSortOrderChanged() {
        const dtos = this.positions.map(x => x.asDto());
        this._hedgePositionsService.saveHedgePositions(dtos, this._selectedPortfolio);
        this.refreshLegs('Calls & Puts');
        this.onSyncPositionsClicked();
    }

    @DetectMethodChanges()
    onAutoSortChanged() {
        if (this.autoSort) {
            this.publishLoading(true);
            setTimeout(() => {
                try {
                    this.positions.forEach(x => x.groupOrder = null);
                    this.afterSortOrderChanged();
                } finally {
                    this.publishLoading(false);
                }
            }, 250);
        }
        this.saveHgState('hg.autoSort');
    }

    private restoreHgState() {
        const keys = getHgStateKeys();

        const userId = this._sessionService.sessionData.userId;

        keys.forEach(key => {

            const v = this._settingsStorageService.getItem(key, userId) as any;

            switch (key) {
                case "hg.strikeWidth":
                    const width = v; //parseInt(v);
                    this.strikeWidth = width;
                    break;
                case "hg.strikeOffset":
                    const offset = v; // parseInt(v);
                    this.strikeOffset = offset;
                    break;
                case "hg.selectedStrategy":
                    this.selectedStrategy = v as any;
                    break;
                case "hg.selectedOptionType":
                    this.selectedOptionType = v as any;
                    break;
                case "hg.baseQty":
                    const baseQty = v; // parseInt(v);
                    this.baseQty = baseQty;
                    break;
                case "hg.autoSort":
                    const autoSort = v; // === 'true';
                    this.autoSort = autoSort;
                    break;
            }
        });
    }

    onLegExpirationChanging(leg: HedgePositionModel, $event: any) {
        if (leg.expirationDescriptor === $event) {
            return;
        }

        if (isVoid($event)) {
            return;
        }

        this.cloneAndRemoveLeg(leg, 'expiration');
    }

    @DetectMethodChanges({isAsync: true})
    async onLegExpirationChanged(pGroup: LegsGroup, leg: HedgePositionModel, change: ExpirationChangeEvent) {

        // DevExtreme also reports initialization and input refreshes. Those must
        // never become working-book edits or rebuild quote subscriptions.
        if (!change?.event || change.value === leg.expirationDescriptor || !this.isLegSeriesReady(leg)) {
            return;
        }

        await this.runWithLoadingIndicator(() => {
            const chain = this._optionsChainService.getCachedChain(leg.underlying);
            if (!this.isLegSeriesReady(leg) || (change.value && !chain?.expirations.includes(change.value))) {
                return;
            }
            this.onLegExpirationChanging(leg, change.value);
            leg.expirationDescriptor = change.value;
            let affected = [leg];

            if (pGroup.expirationsLinked) {

                const notRemovedLegs = pGroup.legs.filter(x => !x.removed);

                for (const aLeg of notRemovedLegs) {

                    if (aLeg.expirationDescriptor === leg.expirationDescriptor) {
                        continue;
                    }

                    this.cloneAndRemoveLeg(aLeg, 'expiration');

                    aLeg.expirationDescriptor = leg.expirationDescriptor;
                }

                affected = pGroup.legs.slice();
            }

            this.updateQuoteSubscriptions(affected);
            this.checkCopilotDivergence();   // M3-6: expiration change on a CoPilot-ADDED leg bypasses refreshLegs
        });
    }

    canReviewChanges() {

        const after = this.positions.map(x => x.asComparisonUnit());
        const before = this._initialPositions.map(x => getIdentity(x));

        const hasAfterDifference = !arraysEqual(after, this._baseState?.after);
        const hasBeforeDifference = !arraysEqual(before, this._baseState?.before);
        const hasChangesSinceLastReview = hasAfterDifference || hasBeforeDifference;

        const enoughForSync = this.canReviewChangesForSync();

        const noExpired = !this.positions.some(x => this.isLegExpired(x));

        return enoughForSync && hasChangesSinceLastReview && noExpired;

    }

    canReviewChangesForSync() {

        if (this.isMergeMode) {
            return false;
        }

        const legsAreValid = this.positions
            .filter(x => !x.removed)
            .every(x => x.isValid());

        if (!legsAreValid) {
            return false;
        }

        const hasDuplicates = this.areThereDuplicateLabelsOrColors();

        if (hasDuplicates) {
            return false;
        }

        return true;
    }

    canSyncPositions() {

        if (!this.isMergeMode) {
            return false;
        }

        const legsAreValid = this.positions
            .filter(x => !x.removed)
            .every(x => x.isValid());

        if (!legsAreValid) {
            return false;
        }

        const hasDuplicates = this.areThereDuplicateLabelsOrColors();

        if (hasDuplicates) {
            return false;
        }

        return true;
    }

    canSyncLabelsAndColors() {

        if (this.isMergeMode) {
            return false;
        }

        const legsAreValid = this.positions
            .filter(x => !x.removed)
            .every(x => x.isValid());

        if (!legsAreValid) {
            return false;
        }

        const hasDuplicates = this.areThereDuplicateLabelsOrColors();

        if (hasDuplicates) {
            return false;
        }

        return true;
    }

    onLegStrikeChanging(leg: HedgePositionModel, $event: number) {
        if (leg.strike === $event) {
            return;
        }

        if (isVoid($event)) {
            return;
        }

        this.cloneAndRemoveLeg(leg, 'strike');
    }

    @DetectMethodChanges()
    onLegStrikeChanged(leg: HedgePositionModel) {

        this.updateQuoteSubscriptions([leg], false, true);

        if (this.autoSort) {
            this.refreshLegs(leg.type);
        }

        this.updateQuoteSubscriptions([leg], true);

        if (leg.strategy === 'BOS') {
            this.recalculateBosStrategy(leg);
        } else if (leg.strategy === 'BOS-Mid') {
            this.recalculateBosMidStrategy(leg);
        } else if (leg.strategy === 'Sponsored Long') {
            this.recalculateSponsoredLongStrategy(leg);
        }

        this.checkCopilotDivergence();   // M3-6: strike edit bypasses refreshLegs when autoSort is off (idempotent when on)
    }

    canMerge() {
        const totalPackages = this.getAllGroups();
        const hasLegChanges = totalPackages
            .some(x => this.doesGroupHaveLegChanges(x));
        return !hasLegChanges || (hasLegChanges && this.isMergeMode);
    }

    private getAllGroups(): LegsGroup[] {
        const callGroups = this._callLegsByGroup || [];
        const putGroups = this._putLegsByGroup || [];
        const totalGroup = callGroups.concat(putGroups);
        return totalGroup;
    }

    getChangesState(): 'Merge' | 'Review' | 'Colors & Labels' {
        if (this.isMergeMode) {
            return 'Merge';
        }

        const someHaveLegChanges = this.getAllGroups()
            .some(x => this.doesGroupHaveLegChanges(x));

        const someHaveLabelColorChanges = this.getAllGroups()
            .some(x => this.doesGroupHaveLabelOrColorChanges(x));

        if (someHaveLabelColorChanges && !someHaveLegChanges) {
            return 'Colors & Labels';
        }

        return 'Review';
    }

    doesGroupHaveAnyChanges(group: LegsGroup): boolean {
        return this.doesGroupHaveLegChanges(group) ||
            this.doesGroupHaveLabelOrColorChanges(group);
    }

    doesGroupHaveLegChanges(group: LegsGroup): boolean {

        const originalGroup =
            this.initialPositions.filter(x => x.groupId === group.groupId);

        if (isVoid(originalGroup)) {
            return true;
        }

        if (originalGroup.length !== group.legs.length) {
            return true;
        }

        const hasSomeTrans = group.legs.some(x => isValidNumber(x.transQty));

        if (hasSomeTrans && !this.isMergeMode) {
            return true;
        }

        const originalTickers = originalGroup.map(x => `${x.ticker}|${x.qty}`);

        const groupTickers = group.legs.filter(x => !x.removed).map(x => {
            const ticker = makeOptionTicker(
                x.expirationDescriptor,
                x.type,
                x.strike,
                'American'
            );
            return `${ticker}|${x.qty}`
        });

        const tickersSame = originalTickers.sort().toString() === groupTickers.sort().toString();

        return !tickersSame;
    }

    @DetectMethodChanges()
    copyHedgeBoxLegs(group: LegsGroup): void {
        const isModified = this.doesGroupHaveLegChanges(group);

        let items: ComboHighlightedItem[];
        let mode: string;

        if (isModified) {
            // Transaction mode: emit trade DELTAS only (the actual orders to execute).
            // Trade qty = newSignedQty - originalSignedQty per leg, filtered for non-zero.
            mode = 'transaction';
            const initial = this.initialPositions.filter(p => p.groupId === group.groupId);
            const trades: { ticker: string, type: 'Call' | 'Put', underlying: string, qty: number }[] = [];

            for (const leg of group.legs) {
                const ticker = leg.getTicker();
                const orig = initial.find(p => p.ticker === ticker);
                const origQty = orig?.qty ?? 0;

                if (leg.removed) {
                    // Sell off the original position entirely.
                    if (origQty !== 0) {
                        trades.push({ ticker, type: leg.type, underlying: leg.underlying, qty: -origQty });
                    }
                    continue;
                }

                // newSignedQty: if user entered transQty, it is signed by current qty's sign
                // (per existing pattern at :2347 `model.qty = l.transQty * Math.sign(l.qty)`).
                // Otherwise leg.qty is the new state.
                const newSignedQty = isValidNumber(leg.transQty)
                    ? leg.transQty * Math.sign(leg.qty)
                    : leg.qty;

                const tradeQty = newSignedQty - origQty;
                if (tradeQty !== 0) {
                    trades.push({ ticker, type: leg.type, underlying: leg.underlying, qty: tradeQty });
                }
            }

            // Initial legs that are no longer present in group.legs (fully removed).
            for (const orig of initial) {
                const stillThere = group.legs.find(l => !l.removed && l.getTicker() === orig.ticker);
                if (!stillThere && !group.legs.find(l => l.getTicker() === orig.ticker)) {
                    trades.push({ ticker: orig.ticker, type: orig.type, underlying: orig.asset, qty: -orig.qty });
                }
            }

            items = trades.map(t => ({
                ticker: t.ticker,
                underlying: t.underlying,
                side: t.qty > 0 ? MarketSide.Buy : MarketSide.Sell,
                netPosition: Math.abs(t.qty),
                itemType: t.type === 'Call' ? PortfolioItemType.Call : PortfolioItemType.Put,
            } as ComboHighlightedItem));
        } else {
            // Original mode: emit hedge as-is from initialPositions.
            mode = 'original';
            const original = this.initialPositions.filter(p => p.groupId === group.groupId);
            items = original.map(pos => ({
                ticker: pos.ticker,
                underlying: pos.asset,
                side: pos.qty > 0 ? MarketSide.Buy : MarketSide.Sell,
                netPosition: Math.abs(pos.qty),
                itemType: pos.type === 'Call' ? PortfolioItemType.Call : PortfolioItemType.Put,
            } as ComboHighlightedItem));
        }

        if (items.length === 0) {
            this._toastr.warning(isModified ? 'No transaction trades to copy' : 'Hedge has no legs to copy');
            console.log(`[ETS-CLIPBOARD] write source=hg-pad mode=${mode} items=0`);
            return;
        }

        const msg: ComboHighlightedUIMessage = {
            items,
            strategyName: group.groupLabel,
            orderParams: {},
        };

        this._clipboardService.put(ETS_CLIPBOARD_LEGS_KEY, msg);
        console.log(`[ETS-CLIPBOARD] write source=hg-pad mode=${mode} items=${items.length} groupId=${group.groupId}`);
        this._toastr.success(isModified ? 'Hedge transaction copied to clipboard' : 'Hedge legs copied to clipboard');
    }

    doesGroupHaveLabelOrColorChanges(group: LegsGroup): boolean {
        if (isVoid(this._initialColorsAndLabels)) {
            return false;
        }

        const initialColorsAndLabel = this._initialColorsAndLabels.get(group.groupId);

        if (isVoid(initialColorsAndLabel)) {
            return false;
        }

        return initialColorsAndLabel.label !== group.groupLabel ||
            initialColorsAndLabel.color !== group.groupColor;
    }

    private publishLoading(isLoading: boolean, isAsync?: boolean) {
        if (!isAsync) {
            this._messageBus.publish({
                topic: 'Hg.Loading',
                payload: {isLoading}
            });
        } else {
            this._messageBus.publishAsync({
                topic: 'Hg.Loading',
                payload: {isLoading}
            });
        }
    }

    private async runWithLoadingIndicator<T>(work: () => Promise<T> | T): Promise<T | undefined> {
        this.publishLoading(true);
        try {
            await delay(50);
            return await Promise.resolve(work());
        } finally {
            setTimeout(() => this.publishLoading(false), 250);
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onAfterStateApplied(payload: { side: "Calls" | "Puts" }) {

        this.publishLoading(true);

        await delay(250);

        const currentPositions = await this._hedgePositionsService
            .getPositionModels(this._selectedPortfolio);

        const initialPositions = currentPositions.map(x => x.asDto());

        this._messageBus.publish({
            topic: 'Hg.InitialPositionsChanged',
            payload: {
                portfolio: this._selectedPortfolio,
                positions: initialPositions
            }
        });

        const type = payload.side === 'Calls' ? 'Call' : 'Put';

        const thisSidePositions = currentPositions.filter(x => x.type === type);

        const otherSidePositions = this.positions.filter(x => x.type !== type);

        this.positions = thisSidePositions.concat(otherSidePositions);

        this.refreshLegsSuppressingDivergence(payload.side);   // M3-6: commit reload preserves the other side's CoPilot refs — NOT a manual edit

        // Guard (owner 2026-06-23): a full CoPilot close empties the reloaded book, and
        // canReviewChangesForSync() returns vacuously true for an empty positions array — which would
        // drive a review of an empty book and trip the grid's "Current configuration make no sense"
        // toast (once per side). Skip the programmatic post-acquire sync when there is no live leg to
        // review (the grid would isVoid(sides)->return a no-op anyway). The MANUAL Review path is
        // untouched, so it keeps that validation toast.
        if (this.positions.some(x => !x.removed) && this.canReviewChangesForSync()) {
            this.onReviewChangesClicked(true);
        }

        await delay(500);

        this.publishLoading(false);


    }

    getGrandTotalsValue(side: 'Call' | 'Put', kind: 'owned' | 'trans') {
        var groups = this.getLegGroups(side);

        if (isVoid(groups)) {
            return undefined;
        }

        const sum = groups
            .map(x => this.getGroupTotalPrice(x, kind))
            .reduce((x, y) => (x || 0) + (y || 0), undefined);

        if (!isValidNumber(sum)) {
            return undefined;
        }

        return sum;
    }

    getGrandTotalsVisibility(side: 'Call' | 'Put' | 'Grand', kind: 'owned' | 'trans'): 'visible' | 'hidden' {

        const groups = side === 'Grand' ? this.getAllGroups() : this.getLegGroups(side);

        const some = groups
            .some(grp => this.getPriceboxVisibility(grp, kind, grp.side) === 'visible');

        return some ? 'visible' : 'hidden';
    }

    getGrandTotalsDisplay(side: 'Call' | 'Put' | 'Grand', kind: 'owned' | 'trans'): 'block' | 'none' {

        if (kind === 'owned') {
            const groups = side === 'Grand' ? this.getAllGroups() : this.getLegGroups(side);

            const some = groups
                .some(grp => this.getOwnPriceboxDisplay() === 'block');

            return some ? 'block' : 'none';
        } else if (kind === 'trans') {
            const groups = side === 'Grand' ? this.getAllGroups() : this.getLegGroups(side);

            const some = groups
                .some(grp => this.getTransPriceboxDisplay('Call') === 'block');

            return some ? 'block' : 'none';
        }
    }

    getGroupTotalPrice(pGroup: LegsGroup, kind?: 'owned' | 'trans'): number {

        if (isVoid(pGroup)) {
            return undefined;
        }

        if (!isValidNumber(pGroup.hcf, true)) {
            const qties = pGroup.legs.map(x => x.absQty);
            const hcf = findHCF(qties);
            pGroup.hcf = hcf;
        }

        const ownershipFilter = (leg: HedgePositionModel) => {

            if (isVoid(kind)) {
                return true;
            }

            if (leg.removed) {
                return kind === 'owned' ? false : true;
            }

            if (leg.selectedForTrans) {
                return true;
            }

            if (this.isOwnedLeg(leg) && kind === 'owned') {
                return true;
            }


            if (!this.isOwnedLeg(leg) && kind === 'trans') {
                return true;
            }

            return false;
        }

        const filteredLegs = pGroup.legs.filter(ownershipFilter);

        const qttiesForHcf = filteredLegs.map(x => {
            if (kind === 'owned') {
                return x.absQty;
            } else {
                if (x.selectedForTrans) {
                    const transQty = x.transQty * Math.sign(x.qty);
                    const netQty = transQty - x.qty;
                    return Math.abs(netQty);
                } else {
                    return x.absQty;
                }
            }
        });

        const hcf = findHCF(qttiesForHcf);

        const ratio = hcf / this.portfolioDefaultQty;

        let legPrices = filteredLegs
            .map(x => {

                const legPx = this.isLegExpired(x) ? 0 : x.price;

                if (!isValidNumber(legPx)) {
                    return null;
                }

                let legQty: number;

                if (x.selectedForTrans && !x.selectedForMerge && kind === 'trans') {
                    const transQty = x.transQty * Math.sign(x.qty);
                    const netQty = transQty - x.qty;
                    legQty = netQty;
                } else {
                    legQty = x.qty;
                }

                const qty = legQty / hcf;

                let px = legPx * qty;

                if (!this.isOwnedLeg(x) && !x.removed || (x.selectedForTrans && !x.selectedForMerge && kind !== 'owned')) {
                    px *= -1;
                }

                return px;
            });

        let totalPx = null;

        if (legPrices.every(lp => isValidNumber(lp))) {
            totalPx = Enumerable.from(legPrices).sum();
        }

        if (isValidNumber(totalPx)) {
            if (isValidNumber(ratio, true)) {
                totalPx = totalPx * ratio;
            }

            // Normalize ES→SPX: ES=$50/point vs SPX=$100/point
            if (pGroup.underlying && pGroup.underlying !== this._baseUnderlying
                && isFuturesTicker(pGroup.underlying)) {
                totalPx = totalPx * 0.5;
            }

            if (Math.abs(totalPx) < Number.EPSILON) {
                totalPx = 0;
            }
        }

        return totalPx;
    }

    private updateQuoteSubscriptions(legs: HedgePositionModel[], initial = false, final = false) {

        const oldTickers = initial ? [] : legs.map(x => x.getTicker());

        legs.forEach(x => x.updateTicker());

        const currentTicker = final ? [] : legs.map(x => x.getTicker());

        this._lastQuoteCache.subscribeTickersDiff(oldTickers, currentTicker);

        legs.forEach(leg => {
            const quote = this._lastQuoteCache.getLastQuote(leg.getTicker());

            if (!isVoid(quote)) {
                leg.price = quote.mid;
            }
        });

        this.fillExpiredPnls(legs);
    }

    private fillExpiredPnls(legs: HedgePositionModel[]) {
        const promises = legs.filter(leg => !isVoid(leg.expiredTicker))
            .map(leg => {
                const expiration = parseOptionTicker(leg.expiredTicker)?.expiration;
                const prms = this._settlementPriceService
                    .getSettlementPriceWithRequest(leg.underlying, expiration)
                    .then(result => {
                        if (!isValidNumber(result)) {
                            return undefined;
                        }
                        const outcomeQty = leg.qty;
                        let delta = (result - leg.strike) * outcomeQty;
                        if (leg.type === 'Put') {
                            delta *= -1;
                        }
                        if (delta < 0) {
                            if (outcomeQty > 0) {
                                delta = 0;
                            }
                        } else if (delta > 0) {
                            if (outcomeQty < 0) {
                                delta = 0;
                            }
                        }
                        let pnl = delta;
                        pnl = pnl / this.portfolioDefaultQty;

                        // Normalize ES→SPX: ES=$50/point vs SPX=$100/point
                        if (leg.underlying && leg.underlying !== this._baseUnderlying
                            && isFuturesTicker(leg.underlying)) {
                            pnl = pnl * 0.5;
                        }

                        leg.expirationPnl = pnl;
                    });
                return prms;
            });

        Promise.all(promises).finally(() => this._changeDetector.detectChanges());
    }

    getPriceBoxStyleInputAttr(value: number) {
        let style = 'text-align: center;'

        if (!isValidNumber(value, true)) {
            style += 'color: inherit';
            return {style};
        }

        if (value > 0) {
            style += 'color: green';
        } else if (value < 0) {
            style += 'color: red';
        } else {
            style += 'color: inherit';
        }

        return {style};
    }

    getPnLColor(value: number) {

        if (!isValidNumber(value, true)) {
            return undefined;
        }

        if (value > 0) {
            return 'green';
        } else {
            return 'red';
        }

    }

    @DetectMethodChanges({isAsync: true})
    async removeWholeGroup(pGroup: LegsGroup, side: 'Call' | 'Put') {

        await this.runWithLoadingIndicator(() => {
            pGroup.legs.forEach(leg => {
                if (this.isOwnedLeg(leg)) {
                    leg.removed = true;
                } else {
                    const ix = this.positions.indexOf(leg);
                    if (ix !== -1) {
                        this.positions.splice(ix, 1);
                    }
                }
            });
            this.refreshLegs(side);
        });
    }

    @DetectMethodChanges({isAsync: true})
    async restoreWholeGroup(pGroup: LegsGroup) {
        await this.runWithLoadingIndicator(() => {
            pGroup.legs.forEach(leg => leg.removed = false);
            this.checkCopilotDivergence();   // M3-6: in-place restore bypasses refreshLegs
        });
    }

    getLegPriceColor(leg: HedgePositionModel, pGroup: LegsGroup) {

        if (!isValidNumber(pGroup.hcf, true)) {
            const qties = pGroup.legs.map(x => x.absQty);
            const hcf = findHCF(qties);
            pGroup.hcf = hcf;
        }

        const qty = leg.qty / pGroup.hcf;
        const px = leg.price;

        const sum = qty * px;

        if (sum === 0) {
            return undefined;
        }

        return sum > 0 ? 'red' : 'green';
    }

    @DetectMethodChanges({isAsync: true})
    async showMergeDialog(side: 'Call' | 'Put', legsGroup: LegsGroup, btnMerge: HTMLDivElement) {

        const sidePackages = this.getLegGroups(side) || [];

        if (sidePackages.length === 0) {
            this._toastr.info('No Packages To Merge With');
            return;
        }

        const packages = sidePackages.map(x => {
            const pckg: HedgePackageToMerge = {
                pckg: x,
                label: x.groupLabel,
                color: x.groupColor,
                groupId: x.groupId,
                selected: x.groupId === legsGroup.groupId
            };
            return pckg;
        });

        const config: HgMergeDialogConfig = {
            target: btnMerge,
            packages,
            mergingPackageLabel: legsGroup.groupLabel,
            comp: this
        }

        let createNewPackage = false;
        try {
            this.isMergeMode = true;
            setTimeout(() => this._changeDetector.detectChanges());
            createNewPackage = await this.mergeDialogCmp.show(config);
        } catch {
            this.cancelMergeOperation();
            return;
        }

        const selectedLegs = sidePackages
            .filter(x => x.selectedForMerge)
            .flatMap(x => x.legs.filter(y => y.selectedForMerge));


        if (selectedLegs.length === 0) {
            this._toastr.error('No Legs Were Selected For Merge');
            this.cancelMergeOperation();
            return;
        }

        this._messageBus.publish({topic: 'Hg.Loading', payload: {isLoading: true}});

        await delay(250);

        const targetGroupId = createNewPackage ? getShortUUID() : legsGroup.groupId;
        const targetLabel = createNewPackage ? undefined : legsGroup.groupLabel;
        const targetColor = createNewPackage ? undefined : legsGroup.groupColor;

        for (const l of selectedLegs) {

            const existingLeg = this.positions
                .find(ep => ep.groupId === targetGroupId
                    && ep.getTicker() === l.getTicker()
                );

            if (!isValidNumber(l.transQty)) {
                if (isVoid(existingLeg)) {
                    l.groupId = targetGroupId;
                    l.label = targetLabel;
                    l.color = targetColor;
                } else {

                    const newQty = existingLeg.qty + l.qty;

                    existingLeg.qty = newQty;

                    if (Math.sign(newQty) === 1) {
                        existingLeg.side = 'Buy';
                    } else if (Math.sign(newQty) === -1) {
                        existingLeg.side = 'Sell';
                    }

                    const ix = this.positions.indexOf(l);
                    if (ix >= 0) {
                        this.positions.splice(ix, 1);
                    }
                }
            } else {

                const asDto = l.asDto();
                const model = await HedgePositionModel.fromDto(asDto, this._optionsChainService);
                model.qty = l.transQty * Math.sign(l.qty);
                model.groupId = targetGroupId;
                model.label = targetLabel;
                model.color = targetColor;

                if (isVoid(existingLeg)) {
                    this.positions.push(model);
                } else {
                    const newQty = existingLeg.qty + model.qty;

                    existingLeg.qty = newQty;

                    if (Math.sign(newQty) === 1) {
                        existingLeg.side = 'Buy';
                    } else if (Math.sign(newQty) === -1) {
                        existingLeg.side = 'Sell';
                    } else {
                        const ix = this.positions.indexOf(existingLeg);
                        if (ix >= 0) {
                            this.positions.splice(ix, 1);
                        }
                    }
                }

                l.qty = l.qty - model.qty;
                l.transQty = undefined;
                l.selectedForTrans = false;
            }

            l.selectedForMerge = false;

            if (l.qty === 0) {
                const lIx = this.positions.indexOf(l);
                if (lIx > -1) {
                    this.positions.splice(lIx, 1);
                }
            }

            if (!isVoid(existingLeg)) {
                const existingLegIx = this.positions.indexOf(existingLeg);
                if (existingLegIx > -1) {
                    this.positions.splice(existingLegIx, 1);
                }
            }
        }

        this.refreshLegs(side);

        setTimeout(() => {
            this._messageBus.publish({topic: 'Hg.Loading', payload: {isLoading: false}});
        }, 500);
    }

    @DetectMethodChanges({isAsync: true})
    async addLeg(pGroup: LegsGroup, side: any) {

        await this.runWithLoadingIndicator(async () => {
            const chain = await this._optionsChainService.getChain(this.tradingInstrument.ticker);

            let expirations = chain.expirations;
            let selectedExpiration = this.selectedExpiration;

            if (!isVoid(pGroup.legs)) {
                expirations = pGroup.legs[0].expirationList;
                selectedExpiration = pGroup.legs
                    .map(x => x.expirationDescriptor)
                    .sort((a, b) => {
                        return a.daysToExpiration - b.daysToExpiration
                    })[0];
            }

            const model = new HedgePositionModel();
            model.underlying = this.tradingInstrument.ticker;
            model.type = side;
            model.expirationList = expirations;
            model.expirationDescriptor = selectedExpiration;
            model.label = pGroup.groupLabel;
            model.groupId = pGroup.groupId;
            model.color = pGroup.groupColor;
            model.groupOrder = pGroup.groupOrder;

            this.positions.push(model);

            this.refreshLegs(side);
        });
    }

    async getShortOptionExpiration(): Promise<OptionExpirationDescriptor> {

        const chain = await this._optionsChainService
            .getChain(this.tradingInstrument.ticker);

        const portfolioPositions
            = await this._apgDataService.getPortfolioPositions(this._selectedPortfolio);

        const pfPositions = portfolioPositions.flatMap(x => x);

        const so = pfPositions.find(x => x.role === 'ShortOption');

        if (isVoid(so)) {
            return isVoid(chain) ? undefined : chain.expirations[0];
        }

        const optionTicker = parseOptionTicker(so.ticker);

        let descriptor = chain.expirations.find(x => x.optionExpirationDate === optionTicker.expiration);

        if (isVoid(descriptor)) {
            descriptor = chain.expirations[0];
        }

        return descriptor;
    }

    @DetectMethodChanges()
    onPackageSelectedForMerge(args: { packageId: string, selected: boolean }) {
        const legsGroup = this.getAllGroups()
            .find(x => x.groupId === args.packageId);

        if (isVoid(legsGroup)) {
            return;
        }

        legsGroup.selectedForMerge = args.selected;
        if (!args.selected) {
            legsGroup.legs.forEach(x => {
                x.selectedForMerge = false;
                this.onSelectedForMergeChanged(x);
            });
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onReviewChangesClicked(byAfterState?: boolean) {

        function processLegs(grp: LegsGroup) {
            grp.legs.forEach(leg => {
                leg.groupId = grp.groupId;
                leg.label = grp.groupLabel;
                leg.color = grp.groupColor;
            });
        }

        const isPortfolioValid = await this.isPortfolioValid();

        if (!isPortfolioValid) {
            return;
        }

        this.getLegGroups('Call')
            .forEach(processLegs);

        this.getLegGroups('Put')
            .forEach(processLegs);

        const groups = Enumerable.from(this.positions)
            .where(x => !x.removed)
            .groupBy(x => x.groupId);

        const labels = groups
            .select(x => x.first().label);

        const distinctLabels = labels.distinct();

        if (labels.count() !== distinctLabels.count()) {
            this._toastr.error('Duplicate group labels are not allowed')
            return;
        }

        const colors = groups.select(x => x.first().color);

        const distinctColors = colors.distinct();

        if (colors.count() !== distinctColors.count()) {
            this._toastr.error('Duplicate group colors are not allowed')
            return;
        }

        const dupLegs = Enumerable.from(this.positions)
            .where(x => !x.removed)
            .select(x => x.asDto())
            .groupBy(x => x.groupId)
            .select(x => {
                const strings = x.select(y => y.ticker).distinct();
                if (strings.count() !== x.count()) {
                    return x.first().label;
                }
                return undefined;
            })
            .where(x => !isVoid(x))
            .toArray();

        if (dupLegs.length > 0) {
            const groups = dupLegs.join(',');
            this._toastr.error(`Please correct duplicate legs for groups: ${groups}`);
            return false;
        }

        this.reviewChangesClicked.emit(byAfterState);
    }

    @DetectMethodChanges()
    afterReviewChanges() {
        const after = this.positions.map(x => x.asComparisonUnit());
        const before = this._initialPositions.map(x => getIdentity(x));

        this._baseState = {before, after};
    }

    @DetectMethodChanges()
    onSyncPositionsClicked() {

        const positions = this.positions.filter(p => !p.removed);

        this._messageBus.publish({
            topic: 'Hg.SyncPositions',
            payload: {
                positions,
                portfolio: this._selectedPortfolio
            }
        });
    }

    isGroupRemoved(pGroup: LegsGroup): boolean {
        return pGroup.legs.every(x => x.removed);
    }

    isOwnedLeg(leg: HedgePositionModel) {
        if (leg.removed) {
            return false;
        }

        const identity = leg.getIdentity();
        if (isVoid(identity)) {
            return false;
        }

        const isInitial = this.initialPositions.some(x => {
            const sameGroup = x.groupId === leg.groupId;
            const sameIdentity = getIdentity(x) === identity;

            return sameGroup && sameIdentity;
        });

        return isInitial;
    }

    getLegPrice(leg: HedgePositionModel, transKind: 'owned' | 'trans'): number {

        let px = leg.price;

        if (!isValidNumber(px)) {
            px = undefined;
        } else {

            if (leg.selectedForTrans) {

                if (transKind === 'trans') {
                    const transQty = leg.transQty * Math.sign(leg.qty);
                    const netQty = transQty - leg.qty;
                    px = leg.price * Math.sign(netQty) * -1;
                } else {
                    px = leg.price * Math.sign(leg.qty);
                }

            } else {
                if (this.isMergeMode || this.isOwnedLeg(leg) || leg.removed) {
                    px = leg.price * Math.sign(leg.qty);
                } else {
                    px = leg.price * Math.sign(leg.qty) * -1;
                }
            }
        }

        return px;
    }

    getPriceboxVisibility(legOrGroup: HedgePositionModel | LegsGroup, kind: 'owned' | 'trans', side?: 'Call' | 'Put') {

        const isLeg = !('legs' in legOrGroup);

        if (isLeg) {

            if (this.isMergeMode) {
                return kind === 'owned';
            }

            legOrGroup = legOrGroup as HedgePositionModel;

            const isOwnedLeg = this.isOwnedLeg(legOrGroup);

            if (kind === 'owned') {
                return isOwnedLeg ? 'visible' : 'hidden';
            }

            if (kind === 'trans') {
                return (isOwnedLeg && !legOrGroup.selectedForTrans) ? 'hidden' : 'visible';
            }
        } else {

            legOrGroup = legOrGroup as LegsGroup;

            if (kind === 'trans') {
                const b = legOrGroup.legs
                    .some(x => {
                        return x.type === side && (!this.isOwnedLeg(x) || x.selectedForTrans);
                    });

                return b ? 'visible' : 'hidden';
            } else {
                const b = legOrGroup.legs.some(x => {
                    return x.type === side && this.isOwnedLeg(x);
                });
                return b ? 'visible' : 'hidden';
            }
        }
    }

    getTransPriceboxDisplay(side: 'Call' | 'Put'): string {
        if (this.isMergeMode) {
            return 'none';
        }

        const b = this.positions
            .some(x => {
                return x.type === side && (!this.isOwnedLeg(x) || x.selectedForTrans);
            });

        return b ? 'block' : 'none';
    }

    getOwnPriceboxDisplay(): string {
        return 'block';
    }

    @DetectMethodChanges()
    private cancelMergeOperation() {

        if (this.isMergeMode) {
            const allGroups = this.getAllGroups();
            const hasChanges = allGroups.some(x => this.doesGroupHaveLegChanges(x));
            if (!hasChanges) {
                this.isMergeMode = false;
            }
        }

        this.positions.forEach(x => x.selectedForMerge = false);

        this.positions.forEach(x => {
            x.selectedForTrans = false;
            x.transQty = undefined;
        });

        this.getLegGroups('Call')?.forEach(x => x.selectedForMerge = false);

        this.getLegGroups('Put')?.forEach(x => x.selectedForMerge = false);
    }

    @DetectMethodChanges()
    onQtyFocusIn(leg: HedgePositionModel) {

        if (!this.isOwnedLeg(leg)) {
            return;
        }

        leg.selectedForTrans = true;
    }

    @DetectMethodChanges()
    onQtyFocusOut(leg: HedgePositionModel) {
        if (this.isMergeMode) {

            if (!isValidNumber(leg.transQty, true)) {
                this._toastr.error('Incorrect Split Qty');
                return;
            }

            if (Math.abs(leg.transQty) > Math.abs(leg.qty)) {
                this._toastr.error('Split Qty Is Too Big');
                return;
            }
        } else {
            if (!isValidNumber(leg.transQty, true)) {
                if (!isNullOrUndefined(leg.transQty)) {
                    this._toastr.error('Transaction Qty Is Not A Valid Number');
                }
                leg.selectedForTrans = false;
                leg.transQty = undefined;
                return;
            }
        }

        this.checkCopilotDivergence();   // M3-6: in-place transQty edit (split box, [(value)]=leg.transQty) bypasses refreshLegs
    }

    @DetectMethodChanges()
    onSelectedForMergeChanged(leg: HedgePositionModel) {
        if (!leg.selectedForMerge) {
            leg.transQty = undefined;
            leg.selectedForTrans = false;
        }
    }

    onQtyBoxInit(transQtyBox: DxNumberBoxComponent) {
        setTimeout(() => {
            transQtyBox.instance.focus();
        });
    }

    @DetectMethodChanges()
    onTranQtyBoxValueChange($event: number, leg: HedgePositionModel) {
        if (!isValidNumber($event, true)) {
            this._toastr.error('Not Valid Transaction/Split Qty');
            return;
        }
        leg.transQty = $event;
    }

    canPasteCombo() {
        return !this.isMergeMode && (this._clipboardService.hasKey('hg.opg-data')) ||
            this._clipboardService.hasKey('hg.pkg-cmprsn') ||
            this._clipboardService.hasKey('hg.opp-data') ||
            this._clipboardService.hasKey(ETS_CLIPBOARD_LEGS_KEY);
    }

    @DetectMethodChanges({isAsync: true})
    async onPasteComboClicked() {

        const combo = this._clipboardService.get('hg.combo') as ComboHighlightedUIMessage;
        const multiCombo = this._clipboardService.get('hg.multi-combo') as MultiLegOrderDataUIMessage;
        const pkgCmprsnData = this._clipboardService.get('hg.pkg-cmprsn') as ComboHighlightedUIMessage[];
        const opgHgData: (ComboHighlightedUIMessage | ComboHighlightedUIMessage[])
            = this._clipboardService.get('hg.opg-data');
        const oppData: ComboHighlightedUIMessage | ComboHighlightedUIMessage[]
            = this._clipboardService.get('hg.opp-data');

        if (combo) {
            await this.runWithLoadingIndicator(async () => {
                await this.pasteComboFromClipboard(combo);
            });
        } else if (oppData) {
            if (Array.isArray(oppData)) {
                await this.runWithLoadingIndicator(async () => {
                    for (const item of oppData) {
                        await this.pasteComboFromClipboard(item);
                        this.refreshLegs('Calls & Puts');
                    }
                    this._changeDetector.detectChanges();
                });
            } else {
                await this.runWithLoadingIndicator(async () => {
                    await this.pasteComboFromClipboard(oppData);
                });
            }
        } else if (multiCombo) {
            await this.runWithLoadingIndicator(async () => {
                await this.pasteComboFromClipboard(multiCombo.main);
                await this.pasteComboFromClipboard(multiCombo.linked);
                await this.pasteComboFromClipboard(multiCombo.secondLinked);
                await this.pasteComboFromClipboard(multiCombo.thirdLinked);
            });
        } else if (pkgCmprsnData) {
            await this.runWithLoadingIndicator(async () => {
                for (const item of pkgCmprsnData) {
                    await this.pasteComboFromClipboard(item);
                    this.refreshLegs('Calls & Puts');
                }
                this._changeDetector.detectChanges();
            });
        } else if (opgHgData) {
            if (Array.isArray(opgHgData)) {
                await this.runWithLoadingIndicator(async () => {
                    for (const opg of opgHgData) {
                        await this.pasteComboFromClipboard(opg);
                        this.refreshLegs('Calls & Puts');
                    }
                    this._changeDetector.detectChanges();
                });
            } else {
                await this.runWithLoadingIndicator(async () => {
                    await this.pasteComboFromClipboard(opgHgData as any);
                });
            }
        } else {
            // FR-12 fallback: universal clipboard key (detailed legs from any source).
            const universal: ComboHighlightedUIMessage | ComboHighlightedUIMessage[] =
                this._clipboardService.get(ETS_CLIPBOARD_LEGS_KEY);
            if (universal) {
                const messages = Array.isArray(universal) ? universal : [universal];

                // Cross-underlying check (FR-21). Pre-flight — runs before overlay (FR-5).
                if (this.tradingInstrument) {
                    for (const m of messages) {
                        const sourceUl = m?.items?.[0]?.underlying;
                        if (sourceUl && sourceUl !== this.tradingInstrument.ticker) {
                            const sourceDisplay = this._tiService.getInstrumentByTicker(sourceUl)?.displayName ?? sourceUl;
                            const targetDisplay = this.tradingInstrument.displayName ?? this.tradingInstrument.ticker;
                            this._toastr.error(`Ticker mismatch: clipboard=${sourceDisplay} target=${targetDisplay}. Paste refused.`);
                            console.log('[ETS-CLIPBOARD] paste rejected target=hg-pad reason=ticker-mismatch');
                            return;
                        }
                    }
                }

                // FR-6: split mixed-type messages so call legs and put legs go to
                // separate hedges (Calls bucket, Puts bucket).
                const splitMessages: ComboHighlightedUIMessage[] = [];
                for (const m of messages) {
                    if (!m?.items?.length) continue;
                    const callItems = m.items.filter(i => parseOptionTicker(i.ticker)?.type === 'Call');
                    const putItems = m.items.filter(i => parseOptionTicker(i.ticker)?.type === 'Put');
                    if (callItems.length > 0 && putItems.length > 0) {
                        splitMessages.push({ ...m, items: callItems });
                        splitMessages.push({ ...m, items: putItems });
                    } else {
                        splitMessages.push(m);
                    }
                }

                // Universal visual rule: legs sorted DESC by strike top-down.
                splitMessages.forEach(m => {
                    if (m?.items) {
                        m.items = [...m.items].sort((a, b) => {
                            const sa = parseOptionTicker(a.ticker)?.strike ?? 0;
                            const sb = parseOptionTicker(b.ticker)?.strike ?? 0;
                            return sb - sa;
                        });
                    }
                });

                const allOk = await this.runWithLoadingIndicator(async () => {
                    let ok = true;
                    for (const m of splitMessages) {
                        const branchOk = await this.pasteComboFromClipboard(m);
                        if (!branchOk) {
                            ok = false;
                        }
                        this.refreshLegs('Calls & Puts');
                    }
                    this._changeDetector.detectChanges();
                    return ok;
                });
                console.log(`[ETS-CLIPBOARD] read target=hg-pad via=universal-key messages=${messages.length} hedgesAfterSplit=${splitMessages.length} allOk=${allOk}`);
                if (allOk) {
                    this._toastr.success('Strategy pasted');
                }
            }
        }

        this.refreshLegs('Calls & Puts');
    }

    async pasteComboFromClipboard(msg: ComboHighlightedUIMessage): Promise<boolean> {

        if (!msg.items) {
            return false;
        }

        if (!isVoid(msg.hedgingGridTransaction)) {
            await this.pasteHedgingGridTransaction(msg);
            return true;
        }

        const comboItems = msg.items
            .filter(x => x.itemType !== PortfolioItemType.Strategy);

        if (comboItems.length === 0) {
            return false;
        }

        const distinctTypesCount = Enumerable.from(comboItems)
            .select(x => x.ticker)
            .distinct()
            .select(x => x.indexOf(' Call ') ? 1 : -1)
            .distinct()
            .count();

        if (distinctTypesCount !== 1) {
            this._toastr.error('Cannot Paste Items Of Mixed Option Types');
            return false;
        }

        const firstItem = comboItems[0] as ComboHighlightedItem;

        const ul = firstItem.underlying;
        const optionType = firstItem.ticker.indexOf('Call') >= 0 ? 'Call' : 'Put';

        // Validate that all items have an allowed underlying
        if (!this.tradingInstrument) {
            this._toastr.error('Cannot Paste Strategy Because Underlying Assets Do Not Match');
            return false;
        }
        const refUl = this.tradingInstrument.ticker;
        const hasDisallowedUnderlying = comboItems.some(function(item) {
            return !this.isAllowedUnderlying(item.underlying, refUl);
        }.bind(this));
        if (hasDisallowedUnderlying) {
            this._toastr.error('Cannot Paste Strategy Because Underlying Assets Do Not Match');
            return false;
        }

        const reconcileEntries = comboItems
            .map(it => parseOptionTicker(it.ticker))
            .filter(t => !isVoid(t))
            .map(t => ({ expiration: t.expiration, strike: t.strike }));

        const missing = await this._optionsChainService.getMissingStrikes(ul, reconcileEntries);

        if (missing.length > 0) {
            this._toastr.warning('Some missing strikes detected. Validating against live quotes…', 'Option Chain', { timeOut: 5000 });
            this._messageBus.publish({topic: 'Hg.Loading', payload: {isLoading: true}});

            let unresolved: { expiration: string; strike: number }[];
            try {
                const r = await this._optionsChainService.tryReconcileMissingStrikes(ul, missing);
                unresolved = r.unresolved;
            } finally {
                this._messageBus.publish({topic: 'Hg.Loading', payload: {isLoading: false}});
            }

            if (unresolved.length > 0) {
                const list = unresolved
                    .map(x => `${x.strike} @ ${makeGuiFriendlyExpirationDate(x.expiration)}`)
                    .join(', ');
                this._toastr.error(`Cannot paste hedge: strike(s) not tradable on live market — ${list}`);
                return false;
            }
        }

        const groupId = getShortUUID();
        const label = msg.strategyName;

        let autoColor = undefined;

        if (optionType === 'Call') {
            autoColor = Enumerable.from(CallHedgePalette).except(this.usedCallColors)
                .firstOrDefault();
        } else if (optionType === 'Put') {
            autoColor = Enumerable.from(PutHedgePalette).except(this.usedPutColors)
                .firstOrDefault();
        }

        const promises = msg.items.map(async item => {

            const ticker = item.ticker;
            const qty = item.netPosition * item.side;

            const dto: HedgePosition = {
                ticker,
                qty,
                label,
                groupId,
                color: autoColor
            };

            const model = await HedgePositionModel.fromDto(dto, this._optionsChainService);

            return model;
        });

        const models = await Promise.all(promises);

        this.positions.push(...models);

        return true;
    }

    private async onHedgesCleared(msg: any) {
        const isReload = msg?.isReload;
        if (isReload) {
            return;
        }
        this.initialPositions = [];
        await this.onClearLegsClicked(true);
        this.afterReviewChanges();
    }

    private areThereDuplicateLabelsOrColors() {

        const labels = this.areThereDuplicateLabels();

        if (labels) {
            return true;
        }

        const colors = this.areThereDuplicateColors();

        if (colors) {
            return true;
        }

        return false;
    }

    private areThereDuplicateLabels(): boolean {
        const groups = Enumerable.from(this.positions)
            .select(x => x.groupId)
            .distinct()
            .toArray();

        const labels = Enumerable.from(this.positions)
            .select(x => x.label?.trim())
            .distinct();

        const result = labels.count() !== groups.length;

        return result;
    }

    private areThereDuplicateColors(): boolean {
        const groups = Enumerable.from(this.positions)
            .select(x => x.groupId)
            .distinct()
            .toArray();

        const colors = Enumerable.from(this.positions)
            .select(x => x.color)
            .distinct();

        const result = colors.count() !== groups.length;

        return result;
    }

    private isAllowedUnderlying(underlying: string, referenceUnderlying: string): boolean {
        if (underlying === referenceUnderlying) {
            return true;
        }
        return this.esHedgingEnabled
            && this._tiService.getInstrumentByTicker(underlying)?.kind === TradingInstrumentKind.Futures
            && underlying.startsWith('ES');
    }

    canAddStrategy() {
        if (isVoid(this.tradingInstrument)) {
            return false;
        }

        if (isVoid(this.selectedStrategy)) {
            return false;
        }

        if (isVoid(this.selectedOptionType)) {
            return false;
        }

        if (!isValidNumber(this.strikeWidth, true)) {
            if (!this.selectedStrategy.startsWith('Option')) {
                return false;
            }
        }

        if (isVoid(this.strikeOffsetMode)) {
            return false;
        }

        if (this.strikeOffsetMode === 'Offset') {
            if (!isValidNumber(this.strikeOffset || 0)) {
                return false;
            }
        } else {
            if (!isValidNumber(this.baseStrike, true)) {
                return false;
            }
        }


        if (!isValidNumber(this.baseQty, true)) {
            return false;
        }

        if (this.isMergeMode) {
            return true;
        }

        return true;
    }

    @DetectMethodChanges()
    changeOffsetMode(offsetMode: 'Offset' | 'Strike') {
        this.strikeOffsetMode = offsetMode;
        if (offsetMode === 'Offset') {
            this.baseStrike = null;
        } else if (offsetMode === 'Strike') {
            this.strikeOffset = null;
        }
    }

    async onSyncColorAndLabelsClicked() {
        this.onSyncPositionsClicked();
    }

    @DetectMethodChanges()
    toggleExpandConfigurator() {
        this.expandConfigurator = !this.expandConfigurator;
    }

    onExpirationsLinkedChanged(pGroup: LegsGroup) {
        this._groupsStateIndex[pGroup.groupId] = pGroup.expirationsLinked;
    }

    @DetectMethodChanges({isAsync: true})
    async resetGroup(pGroup: LegsGroup) {

        await this.runWithLoadingIndicator(async () => {
            const initialPositions = this._initialPositions.filter(x => x.groupId === pGroup.groupId);

            const promises = initialPositions
                .map(ip => HedgePositionModel.fromDto(ip, this._optionsChainService));

            const models = await Promise.all(promises);

            let poses = this.positions
                .filter(x => x.groupId !== pGroup.groupId)
                .concat(models);

            this.positions = poses;

            delete this._groupsStateIndex[pGroup.groupId];

            this.refreshLegs(pGroup.side);
        });
    }

    isOwnedGroup(pGroup: LegsGroup) {
        return this._initialPositions.some(x => x.groupId === pGroup.groupId);
    }

    canRevertGroup(pGroup: LegsGroup) {
        return this.doesGroupHaveAnyChanges(pGroup) && this.isOwnedGroup(pGroup);
    }

    getGrandTotal(value: number | undefined, value2: number | undefined) {
        if (isValidNumber(value) || isValidNumber(value2)) {
            return (value || 0) + (value2 || 0);
        }

        return undefined;
    }

    private async pasteHedgingGridTransaction(msg: ComboHighlightedUIMessage) {

        const hedgePositionModels = this.positions
            .filter(x => x.groupId === msg.hedgingGridTransaction.groupId);

        if (hedgePositionModels.length === 0) {
            this._toastr.error('Operation Failed: cannot find hedge positions related to transaction');
            return;
        }

        const groupLabel = hedgePositionModels[0].label;
        const groupColor = hedgePositionModels[0].color;
        const groupId = hedgePositionModels[0].groupId;

        for (let item of msg.items) {

            const existing = hedgePositionModels
                .find(x => x.getTicker() === item.ticker);

            if (isVoid(existing)) {

                const ticker = item.ticker;
                const qty = item.netPosition * item.side;

                const dto: HedgePosition = {
                    ticker,
                    qty,
                    color: groupColor,
                    label: groupLabel,
                    groupId
                };

                const model = await HedgePositionModel
                    .fromDto(dto, this._optionsChainService);

                this.positions.push(model);

                continue;
            }

            const netQty = (item.netPosition * item.side) + existing.qty;

            if (netQty === 0) {
                existing.removed = true;
                continue;
            }

            if (Math.sign(netQty) !== Math.sign(existing.qty)) {
                const clone = existing.clone();
                existing.removed = true;
                clone.qty = netQty;
                clone.side = clone.qty > 0 ? 'Buy' : 'Sell';
                this.positions.push(clone);
                continue;
            }

            existing.transQty = Math.abs(netQty);
            existing.selectedForTrans = true;

        }
    }

    private recalculateBosStrategy(leg: HedgePositionModel) {
        const legs = Enumerable
            .from(this.positions)
            .where(x => x.groupId === leg.groupId)
            .where(x => x.strategy === 'BOS');


        if (legs.count() === 0) {
            return;
        }

        const optionType = leg.type;

        if (!(optionType === 'Call' || optionType === 'Put')) {
            return;
        }

        let debitSpread: Enumerable.IEnumerable<HedgePositionModel>;

        let creditSpread: Enumerable.IEnumerable<HedgePositionModel>;

        if (optionType === 'Call') {
            debitSpread = legs.reverse().take(2);
            creditSpread = legs.reverse().skip(2).take(2);
        } else if (optionType === 'Put') {
            debitSpread = legs.take(2);
            creditSpread = legs.skip(2).take(2);
        }

        const debitSpreadWidth = debitSpread
            .select((x, ix) => (x.strike as number) * ((ix % 2 === 0) ? 1 : -1))
            .aggregate(0, (a: number, b: number) => a - b);

        const creditSpreadWidth = creditSpread
            .select((x, ix) => (x.strike as number) * ((ix % 2 === 0) ? 1 : -1))
            .aggregate(0, (a: number, b: number) => a - b);

        const creditSpreadQty = Math.abs(debitSpreadWidth) / Math.abs(creditSpreadWidth);

        if (isValidNumber(creditSpreadQty)) {
            creditSpread.forEach(x => x.qty = creditSpreadQty);
        }
    }

    private recalculateBosMidStrategy(leg: HedgePositionModel) {
        const legs = Enumerable
            .from(this.positions)
            .where(x => x.groupId === leg.groupId)
            .where(x => x.strategy === 'BOS-Mid');

        if (legs.count() === 0) {
            return;
        }

        const optionType = leg.type;

        if (!(optionType === 'Call' || optionType === 'Put')) {
            return;
        }

        // BOS-Mid has fixed 3:1 ratio — credit spread legs always have same abs qty
        let creditSpread: Enumerable.IEnumerable<HedgePositionModel>;
        if (optionType === 'Call') {
            creditSpread = legs.skip(1).take(2);
        } else {
            creditSpread = legs.reverse().skip(1).take(2);
        }

        const absQty = creditSpread.select(x => Math.abs(x.qty)).min();
        if (isValidNumber(absQty) && absQty > 0) {
            creditSpread.forEach(x => {
                x.qty = x.qty > 0 ? absQty : -absQty;
            });
        }
    }

    private recalculateSponsoredLongStrategy(leg: HedgePositionModel) {

        const legs = Enumerable
            .from(this.positions)
            .where(x => x.groupId === leg.groupId)
            .where(x => x.strategy === 'Sponsored Long');


        if (legs.count() === 0) {
            return;
        }

        const optionType = leg.type;

        if (!(optionType === 'Call' || optionType === 'Put')) {
            return;
        }

        let debitSpread: Enumerable.IEnumerable<HedgePositionModel>;

        let creditSpread: Enumerable.IEnumerable<HedgePositionModel>;

        if (optionType === 'Call') {
            debitSpread = legs.reverse().take(2);
            creditSpread = legs.reverse().skip(1).take(2);
        } else if (optionType === 'Put') {
            debitSpread = legs.take(2);
            creditSpread = legs.skip(1).take(2);
        }

        const debitSpreadWidth = debitSpread
            .select((x, ix) => (x.strike as number) * ((ix % 2 === 0) ? 1 : -1))
            .aggregate(0, (a: number, b: number) => a - b);

        const creditSpreadWidth = creditSpread
            .select((x, ix) => (x.strike as number) * ((ix % 2 === 0) ? 1 : -1))
            .aggregate(0, (a: number, b: number) => a - b);

        const creditSpreadQty = Math.abs(debitSpreadWidth) / Math.abs(creditSpreadWidth);

        if (isValidNumber(creditSpreadQty)) {
            creditSpread.forEach(x => x.qty = creditSpreadQty);
        }
    }

    getLabelInputAttr(group: LegsGroup) {

        const styleObj = {style: 'text-align: center;'};

        if (isVoid(group)) {
            return styleObj;
        }

        const warningStyle = 'background: yellow; color: black;';

        if (isVoid(group.groupLabel)) {
            styleObj.style = styleObj.style + warningStyle;
            return styleObj;
        }

        const hasDuplicate = this.positions.filter(x => x.groupId !== group.groupId)
            .some(x => x.label === group.groupLabel);

        if (hasDuplicate) {
            styleObj.style = styleObj.style + warningStyle;
        }

        return styleObj;
    }

    showCustomSortDialog() {
        const calls = this.getLegGroups('Call');
        const puts = this.getLegGroups('Put');

        const config: HedgesSortingDialogConfig = {
            calls,
            puts
        };

        this.hedgesSortingDialog.show(config);
    }

    @DetectMethodChanges()
    onHarvestClicked(side: any, pGroup: LegsGroup, btnMerge: HTMLDivElement) {

        if (pGroup.legs.length === 1) {
            pGroup.legs[0].selectedForHarvest = true;
            this.showHarvestDialog(pGroup);
            return;
        }

        pGroup.selectedForHarvest = !pGroup.selectedForHarvest;

        if (!pGroup.selectedForHarvest) {
            pGroup.legs.forEach(x => x.selectedForHarvest = false);
        }
    }

    @DetectMethodChanges()
    onHarvestMeClicked(pGroup: LegsGroup) {
        this.showHarvestDialog(pGroup, true);
    }

    @DetectMethodChanges({isAsync: true})
    private async showHarvestDialog(pGroup: LegsGroup, ignoreSpreadValidation = false) {
        const legsForHarvest = pGroup.legs.filter(x => x.selectedForHarvest);

        if (legsForHarvest.length === 1) {
            if (legsForHarvest[0].qty <= 0) {
                this._toastr.error('Only long single leg can be harvested');
                return;
            }
        }

        const numberOfSides = Enumerable.from(legsForHarvest)
            .select(x => x.side)
            .distinct()
            .count();

        if (!ignoreSpreadValidation) {
            if (numberOfSides < 2 && pGroup.legs.length > 1) {
                this._toastr.error('Same Side Legs Are Not Allowed For Harvesting');
                return;
            }
        }
        const numberOfTypes = Enumerable.from(legsForHarvest)
            .select(x => x.type)
            .distinct()
            .count();

        if (numberOfTypes > 1 && pGroup.legs.length > 1) {
            this._toastr.error('Different Type Of Options (Calls/Puts) Are Not Allowed For Harvesting');
            return;
        }

        const config: OrdersChainDialogConfig = {
            legs: legsForHarvest,
            groupName: pGroup.groupLabel,
            groupColor: pGroup.groupColor,
            group: pGroup,
            copyOrdersService: this._copyOrdersToService
        };

        await this.ordersChainDialog.show(config);

        pGroup.selectedForHarvest = false;

        pGroup.legs.forEach(x => x.selectedForHarvest = false);
    }

    @DetectMethodChanges()
    onSelectedForHarvestChanged(leg: HedgePositionModel, pGroup: LegsGroup) {
        const length = pGroup.legs.filter(x => x.selectedForHarvest).length;
        if (length < 2) {
            return;
        }
        setTimeout(() => this.showHarvestDialog(pGroup));
    }

    isHarvestMeButtonVisible(leg: HedgePositionModel, pGroup: LegsGroup) {
        if (!leg?.selectedForHarvest) {
            return false;
        }

        if (pGroup.legs.length === 1) {
            return false;
        }

        if (pGroup.legs.filter(x => x.selectedForHarvest).length > 1) {
            return false;
        }

        return leg.selectedForHarvest;
    }

    private async isPortfolioValid(): Promise<boolean> {

        // Independent HG portfolios (CR-20260606-001) have no APG positions to validate;
        // Review is always available (still subject to the HG-leg expiry check).
        if (this._selectedPortfolio?.isIndependent) {
            return true;
        }

        const portfolioPositions
            = await this._apgDataService.getPortfolioPositions(this._selectedPortfolio);

        if (!isVoid(portfolioPositions)) {
            const allPortfolioPositions = portfolioPositions.flatMap(x => x);

            const legsValidnessPromises = allPortfolioPositions.map(async pp => {
                const ticker = parseOptionTicker(pp.ticker);
                if (!isVoid(ticker)) {
                    const chain = await this._optionsChainService.getChain(ticker.underlying);
                    if (!isVoid(chain)) {
                        const isValidExpiration =
                            chain.expirations.some(x => x.optionExpirationDate === ticker.expiration);
                        return isValidExpiration;
                    }
                }
            });

            const legsValidness = await Promise.all(legsValidnessPromises)

            const someNotValid = legsValidness.some(x => !x);

            if (someNotValid) {
                this._toastr.warning('Some Of The Portfolio Positions Are Expired. "Review Changes" Will Not Be Available');
            }

            return !someNotValid;

        } else {
            this._toastr.error('Cannot Determine Portfolio Positions')
        }
    }

    isLegExpired(leg: HedgePositionModel): boolean {
        let isExpired = false;
        const ticker = leg.getTicker();
        if (isVoid(ticker)) {
            return !isVoid(leg.expiredTicker);
        } else {
            isExpired = this._optionsChainService.isOptionExpired(ticker);
        }
        return isExpired;
    }

    getExpiredLegExpirationText(leg: HedgePositionModel) {

        let expirationDate = leg.expiration;

        if (isVoid(expirationDate)) {
            const optionTicker = parseOptionTicker(leg.expiredTicker);
            if (isVoid(optionTicker)) {
                return "EXPIRED";
            }
            expirationDate = optionTicker.expiration;
        }

        let date = makeGuiFriendlyExpirationDate(expirationDate);
        date = date + ' (EXPIRED)'
        return date;
    }

    isHedgeExpired(pGroup: LegsGroup) {
        return pGroup.legs.some(x => this.isLegExpired(x));
    }

    isHedgeFullyExpired(pGroup: LegsGroup) {
        return pGroup.legs.every(x => this.isLegExpired(x))
    }

    getHedgeExpirationPnl(pGroup: LegsGroup) {
        const numbers = pGroup.legs.map(x => x.expirationPnl);

        const total = numbers.reduce((p, c) => p + c, 0);

        return total;
    }

    runExpirationChecker() {
        this._interval = setInterval(() => {
            const expiredPositions = this.positions
                .filter(x => this._optionsChainService.isOptionExpired(x.getTicker()))
                .filter(x => isVoid(x.expiredTicker) || !isValidNumber(x.expirationPnl));

            if (expiredPositions.length === 0) {
                return;
            }

            expiredPositions.forEach(x => {
                x.expiredTicker = x.getTicker();
            });

            this.fillExpiredPnls(expiredPositions);

        }, 1000) as any;
    }


    @DetectMethodChanges()
    onUnderlyingFilterChanged($event: DxValueChanged<string>) {
        if (!$event.event) return;
        if ($event.value === 'All Hedges') {
            this.filterUnderlying = null;
        } else {
            const entry = this._underlyingTickerDisplayMap.find(x => x.displayName === $event.value);
            this.filterUnderlying = entry?.ticker || null;
        }
        this.refreshLegs('Both');
    }

    @DetectMethodChanges({isAsync: true})
    async onExpirationFilterChanged($event: OptionExpirationDescriptor) {

        const sameFilter = $event === this.filterExpiration;

        if (sameFilter) {
            return;
        }

        await this.runWithLoadingIndicator(() => {
            this.filterExpiration = $event;

            this.refreshLegs('Both');
        });
    }

    private refreshFilterExpirations() {
        let positionsEnum = Enumerable.from(this.positions)
            .where(x => !isVoid(x.expirationDescriptor));

        if (!isVoid(this.filterUnderlying)) {
            positionsEnum = positionsEnum.where(x => x.underlying === this.filterUnderlying);
        }

        const distinctExpirations = positionsEnum
            .select(x => x.expirationDescriptor)
            .orderBy(x => x.optionExpirationDate)
            .distinct();

        this.filterExpirationList = distinctExpirations.toArray();

        if (!isVoid(this.filterExpiration)
            && !this.filterExpirationList.some(x => x.optionExpirationDate === this.filterExpiration.optionExpirationDate)) {
            this.filterExpiration = undefined;
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onFilterButtonClick() {
        await this.runWithLoadingIndicator(() => {
            this.filterExpiration = undefined;
            this.refreshLegs('Both');
        });
    }

    // ===== CoPilot facade (IHedgingGridCopilotFacade; ADR-025, CR-20260619-001) ============
    // The grid-owned seam the <ets-hg-copilot> child consumes via @Input() ctx. Delegates to this
    // section's own state/primitives; NO internal-logic change. M3-1 implements the four READ
    // methods + safe no-op placeholders for the two grid-mutating methods (Preview lands in M3-2,
    // un-Preview in M3-3). Reversibility rule (ADR-025): never route through afterReviewChanges /
    // onAcquireRequested — those are the commit path.

    // Provenance: the exact HedgePositionModel instances CoPilot placed, per Solution. Keyed by model
    // REFERENCE (this.positions is reassigned wholesale by other grid methods, so array identity is not
    // a safe key — verified, ADR-025). M3-2 records added legs here; M3-3 extends it for modified hedges.
    private readonly _copilotLedger = new CopilotProvenanceLedger<HedgePositionModel>();

    // Which EXISTING book legs CoPilot took into modification state, per Solution (by reference). Disjoint
    // from the ledger (which holds the new 'open' legs CoPilot ADDED). FR-71/78.
    private readonly _copilotModifiedLegs: { [solutionId: string]: HedgePositionModel[] } = {};

    // The PRISTINE pre-any-CoPilot-modification overlay of each owned leg, keyed by reference. Captured the
    // first time a Solution modifies the leg; un-Preview restores from it and clears it, so a full teardown
    // always returns owned legs to pristine (no residue). NOTE (M3-4): the OQ-13 block in applySolution now
    // prevents two previewed Solutions from concurrently modifying the SAME owned leg (as long as all mutation
    // routes through applySolution), so at most one Solution maps to a given leg here — the SHARED-across-
    // Solutions design (and the isLegModifiedByAnotherSolution guard below) are now belt-and-suspenders, kept
    // as a safe forward-compatible invariant rather than load-bearing.
    private readonly _copilotLegBaseline = new Map<HedgePositionModel, CopilotLegOverlayPhoto>();

    // Solutions whose applySolution is mid-flight, to reject a re-entrant apply of the SAME Solution
    // during the options-chain await window (the child sets `previewed` only after the await resolves).
    private readonly _copilotApplyInFlight = new Set<string>();

    // M3-6 divergence warning (FR-97..103, NFR-23). CoPilot is PASSIVE (owner directive): the GRID detects
    // the user's FIRST manual leg change made on top of CoPilot-suggested legs and warns ONCE — the edit is
    // NEVER prevented. armed = not yet warned this Conversation; re-armed when CoPilot's legs are fully torn
    // down (removeCopilotLegs), which covers every Conversation boundary (new-conversation/portfolio/reload).
    private _copilotDivergenceArmed = true;
    // true while a NON-user repaint is in progress (CoPilot's own Preview/un-Preview, or a programmatic
    // commit/sync reload that PRESERVES CoPilot leg references) — the divergence check skips these so only
    // a genuine USER manual edit warns.
    private _suppressDivergenceCheck = false;

    // ADR-028 independent UI gate — the same secure-element the host *ngIf checks
    // (HedgingGridComponent.isCoPilotAvailable). A self-contained check, not a hardcoded true.
    isEntitled(): boolean {
        return this._accessControlService.isSecureElementAvailable('3ffd6a93-0d5a-4688-80ce-1c4d3d730043');
    }

    // The CURRENT working state projected to the wire shape (ADR-031), read fresh per Run and sent
    // on RunCoPilotSkill.positions. Reuses asDto() so the transQty modification overlay + Buy/Sell
    // signing are honored exactly as the grid's own serialization. Soft-removed legs (removed=true)
    // are being closed by the user → excluded from the state shown to the skill.
    //
    // Hedge = group of legs treated together: if a SINGLE leg in a group is touched (any pending user
    // change), the WHOLE hedge is touched and every leg of it is excluded from the bundle. Otherwise the
    // skill would still see the un-touched legs of a partially-modified hedge and could author a Solution
    // acting on them — the very bug this filter exists to prevent. The skill only sees clean hedges.
    readWorkingStatePositions(): CoPilotPositionDto[] {
        const touched = this.getTouchedOwnedGroupIds();
        const out: CoPilotPositionDto[] = [];
        (this.positions || []).forEach(m => {
            if (m.removed) {
                return;
            }
            if (!isVoid(m.groupId) && touched[m.groupId]) {
                return;   // any leg of a touched hedge is off-limits to the skill (whole-hedge atomicity)
            }
            const dto = m.asDto();   // signed, transQty-aware qty + the option ticker
            out.push({
                ticker: dto.ticker,
                underlying: m.underlying,
                asset: m.underlying,        // HedgePosition.asset === underlying (model has no separate asset; asDto():96)
                quantity: dto.qty,          // signed net (long > 0, short < 0)
                strike: isValidNumber(dto.strike) ? dto.strike : null,
                expiry: isVoid(dto.expiration) ? null : dto.expiration,
                optionType: isVoid(m.type) ? null : m.type,
                avgPrice: null,             // model has no average-cost field (price is a live mid — a different thing)
                label: m.label || '',
                groupId: m.groupId || ''
            });
        });
        return out;
    }

    // Groups with pending user changes (added/removed/resized legs, transQty set). Such hedges are
    // mid-modification and must NOT surface as "available" book hedges to the CoPilot questionnaires —
    // proposing CoPilot work on top of an in-flight user edit would conflict with the user's intent.
    // Uses the grid's own change predicate (doesGroupHaveLegChanges) so the definition of "touched"
    // stays in lock-step with the rest of the panel.
    //
    // Derives groups DIRECTLY from this.positions — not from the _callLegsByGroup / _putLegsByGroup cache
    // and not from getAllGroups(). Two reasons: (1) refreshLegs() — which runs on every leg add/remove/
    // resize — invalidates that cache to undefined, so the cache is empty at the exact moment a touch just
    // happened (the moment the predicate matters most); (2) getAllGroups() honours filterExpiration /
    // filterUnderlying, so off-filter touched hedges would be invisible to the check. The predicate
    // doesGroupHaveLegChanges only reads group.groupId + group.legs, so a synthetic LegsGroup built off
    // positions feeds it correctly. Whole-hedge atomicity: ANY touched leg flags the WHOLE groupId.
    private getTouchedOwnedGroupIds(): { [groupId: string]: boolean } {
        const byGroup: { [gid: string]: HedgePositionModel[] } = {};
        (this.positions || []).forEach(leg => {
            const gid = leg.groupId;
            if (isVoid(gid)) {
                return;
            }
            if (!byGroup[gid]) {
                byGroup[gid] = [];
            }
            byGroup[gid].push(leg);
        });
        const touched: { [groupId: string]: boolean } = {};
        Object.keys(byGroup).forEach(gid => {
            const synthetic = { groupId: gid, legs: byGroup[gid] } as LegsGroup;
            if (this.doesGroupHaveLegChanges(synthetic)) {
                touched[gid] = true;
            }
        });
        return touched;
    }

    // Distinct expiration dates of the EXISTING UNTOUCHED book (owned legs of clean groups) — constrains
    // the Close multi-select (FR-19/148). yyyy-MM-dd -> local-midnight Date so the Close tab's
    // toCalendarDateString round-trips (no UTC off-by-one). Hedges in user-modification state are
    // excluded so their expiration only contributes a date if at least one CLEAN hedge expires then.
    readBookDates(): Date[] {
        const touched = this.getTouchedOwnedGroupIds();
        const seen: { [key: string]: boolean } = {};
        const out: Date[] = [];
        (this.positions || []).forEach(leg => {
            if (!this.isOwnedLeg(leg)) {
                return;
            }
            if (touched[leg.groupId]) {
                return;
            }
            const exp = leg.expiration;   // 'yyyy-MM-dd'
            if (isVoid(exp) || seen[exp]) {
                return;
            }
            seen[exp] = true;
            const dt = DateTime.fromFormat(exp, 'yyyy-MM-dd');
            if (dt.isValid) {
                out.push(dt.toJSDate());
            }
        });
        return out;
    }

    // Expirations for the ADJUST tab's selector (CR-20260814-001). Part of IHedgingGridCopilotFacade;
    // the live CoPilot ctx is the Matrix (HedgingGridComponent), not the Pad, so in practice the
    // Matrix's own copilotReadAdjustDates answers this — the same reason the other facade members
    // below are present.
    //
    // Delegating to readBookDates is safe HERE and only here: the Pad derives its dates by walking
    // real owned legs, so every date it names is already backed by a held leg. The Matrix could not
    // do this — its readBookDates path can synthesise an expiration with no held legs, which is
    // exactly why copilotReadAdjustDates exists as a separate walk over there.
    readAdjustDates(): Date[] {
        return this.readBookDates();
    }

    // The EXISTING UNTOUCHED book hedges (owned legs grouped by groupId, clean state) the Close tab
    // may target (FR-21/22). label is a DISPLAY name (memory: ui-display-names-not-raw-tickers) — the
    // hedge's own label, or the resolved underlying display name when unlabelled; never a raw ticker.
    // Hedges the user has started modifying (added/removed legs, transQty, etc.) are excluded — they
    // are already mid-edit and must not appear as available CoPilot targets.
    readBookHedges(): CoPilotBookHedge[] {
        const touched = this.getTouchedOwnedGroupIds();
        const byGroup: { [groupId: string]: HedgePositionModel } = {};
        const order: string[] = [];
        (this.positions || []).forEach(leg => {
            if (!this.isOwnedLeg(leg)) {
                return;
            }
            const gid = leg.groupId;
            if (isVoid(gid) || byGroup[gid] || touched[gid]) {
                return;
            }
            byGroup[gid] = leg;
            order.push(gid);
        });
        return order.map(gid => ({ id: gid, label: this.makeBookHedgeLabel(byGroup[gid]) }));
    }

    private makeBookHedgeLabel(leg: HedgePositionModel): string {
        const ulDisplay = this._tiService.getInstrumentByTicker(leg.underlying)?.displayName || leg.underlying;
        return isVoid(leg.label) ? ulDisplay : leg.label;
    }

    // --- Preview a Solution (M3-2 add path + M3-3 existing-hedge modification) -----------
    // Place a Solution's transaction legs onto the working state, reversibly + ATOMICALLY (NFR-10,
    // all-or-none): on any failure NOTHING is placed and the working state is byte-identical to
    // pre-Preview. NEVER routes through afterReviewChanges / onAcquireRequested (the commit path).
    //
    // Each leg is classified by its ACTION EFFECT (FR-71/72; the skill states intent explicitly):
    //   open                 -> ADD a new leg under one CoPilot-owned groupId (signed qty by side);
    //   close                -> close the WHOLE matching OWNED book leg (removed);
    //   increase / decrease  -> resize the matching OWNED book leg (transQty up / down; <= 0 => closed).
    // close/increase/decrease MUST match an existing owned book leg by full ticker; a leg with no match
    // is an exception => the whole Solution is rejected (missing-target-leg). open legs are never matched
    // (always new). User-authored + other-Solution legs are NEVER targeted (FR-79).
    //
    // Atomicity via a two-phase split: PHASE 1 (all awaits — parse / strike-reconcile / build open-leg
    // models; match the existing targets) mutates nothing, so any failure leaves the grid untouched;
    // PHASE 2 (pure synchronous) applies the resolved plan and cannot fail. A modified owned leg is
    // photographed (pristine, shared across Solutions) BEFORE mutation so un-Preview restores its exact
    // pre-Preview overlay (FR-78). Legs acting on the same owned leg are coalesced (one photo + one
    // mutation per leg). Leg.ticker is the full host option ticker (placement key, matches getTicker()).
    async applySolution(solutionId: SolutionId, legs: Leg[]): Promise<ApplyResult> {
        if (isVoid(legs) || legs.length === 0) {
            return { applied: false, reason: 'unplaceable-leg' };
        }
        if (this._copilotApplyInFlight.has(solutionId)) {
            return { applied: false, reason: 'unplaceable-leg' };   // re-entrant apply of the same Solution
        }
        this._copilotApplyInFlight.add(solutionId);
        try {
            // PHASE 1 — resolve every leg WITHOUT mutating this.positions.
            const resByLeg = new Map<HedgePositionModel, { hasClose: boolean; delta: number }>();  // target -> resolution
            const modOrder: HedgePositionModel[] = [];
            const addSpecs: { ticker: string; qty: number; underlying: string; expiration: string; strike: number; isCall: boolean }[] = [];

            for (const leg of legs) {
                if (leg.effect === 'open') {
                    // FR-72: a brand-new leg. Its ticker must be a well-formed option ticker (else unplaceable).
                    const parsed = parseOptionTicker(leg.ticker);
                    if (isVoid(parsed) || isVoid(parsed.underlying) || isVoid(parsed.expiration) || !isValidNumber(parsed.strike)) {
                        return { applied: false, reason: 'unplaceable-leg' };
                    }
                    const qty = leg.side === 'sell' ? -Math.abs(leg.quantity) : Math.abs(leg.quantity);
                    addSpecs.push({
                        ticker: leg.ticker, qty,
                        underlying: parsed.underlying, expiration: parsed.expiration,
                        strike: parsed.strike, isCall: parsed.type === 'Call'
                    });
                    continue;
                }
                // Fail-safe: a non-open leg whose action did not parse to a known effect ('unknown') is
                // rejected — NEVER silently mis-mapped to a real grid op (the backend already validates).
                if (leg.effect !== 'close' && leg.effect !== 'increase' && leg.effect !== 'decrease') {
                    return { applied: false, reason: 'unplaceable-leg' };
                }
                // FR-71: close / increase / decrease act on an EXISTING owned book leg (matched by full ticker).
                const existing = this.positions.find(p => this.isOwnedLeg(p) && p.getTicker() === leg.ticker);
                if (isVoid(existing)) {
                    // OQ-13: a prior CoPilot CLOSE sets the owned leg removed=true, so isOwnedLeg() no longer
                    // matches it — yet the hedge IS in the book, just locked by that other Solution. Report the
                    // Adjust ⊥ Close conflict (hedge-locked), not "not in book" (missing-target-leg).
                    if (this.isTickerModifiedByAnotherSolution(leg.ticker)) {
                        return { applied: false, reason: 'hedge-locked' };
                    }
                    return { applied: false, reason: 'missing-target-leg' };   // exception: target not in the book
                }
                let res = resByLeg.get(existing);
                if (!res) {
                    res = { hasClose: false, delta: 0 };
                    resByLeg.set(existing, res);
                    modOrder.push(existing);
                }
                if (leg.effect === 'close') {
                    res.hasClose = true;
                } else if (leg.effect === 'increase') {
                    res.delta += Math.abs(leg.quantity);
                } else {  // 'decrease'
                    res.delta -= Math.abs(leg.quantity);
                }
            }

            // OQ-13 (Adjust ⊥ Close) — owner ruling 2026-06-19. An owned book hedge may be in CoPilot
            // modification state for AT MOST ONE Solution at a time: if any target leg is already being
            // modified by a DIFFERENT currently-previewed Solution, BLOCK the whole Preview. This is the
            // STRUCTURAL exception to FR-84 ("keep both contradictory Solutions") — you cannot adjust and
            // close the same hedge at once. _copilotModifiedLegs[solutionId] is not recorded until PHASE 2,
            // so the scan sees ONLY other Solutions here. Checked pre-mutation AND before the option-chain
            // awaits, so a doomed Preview spends no chain calls and the grid stays byte-identical (NFR-10).
            for (const existing of modOrder) {
                if (this.isLegModifiedByAnotherSolution(existing)) {
                    return { applied: false, reason: 'hedge-locked' };
                }
            }

            // Build the new ('open') leg models + validate their strikes are tradable. Any failure => nothing placed.
            let addModels: HedgePositionModel[] = [];
            if (addSpecs.length > 0) {
                const entriesByUl: { [ul: string]: { expiration: string; strike: number }[] } = {};
                addSpecs.forEach(s => {
                    (entriesByUl[s.underlying] = entriesByUl[s.underlying] || []).push({ expiration: s.expiration, strike: s.strike });
                });
                try {
                    const underlyings = Object.keys(entriesByUl);
                    for (const ul of underlyings) {
                        const missing = await this._optionsChainService.getMissingStrikes(ul, entriesByUl[ul]);
                        if (missing.length > 0) {
                            const reconciled = await this._optionsChainService.tryReconcileMissingStrikes(ul, missing);
                            if (reconciled.unresolved.length > 0) {
                                return { applied: false, reason: 'unplaceable-leg' };
                            }
                        }
                    }
                } catch {
                    return { applied: false, reason: 'unplaceable-leg' };   // chain unreachable -> fail safe
                }

                const groupId = getShortUUID();                  // one CoPilot-owned group per Solution
                const label = 'CoPilot';
                const callColor = Enumerable.from(CallHedgePalette).except(this.usedCallColors).firstOrDefault();
                const putColor = Enumerable.from(PutHedgePalette).except(this.usedPutColors).firstOrDefault();
                try {
                    addModels = await Promise.all(addSpecs.map(s => {
                        const dto: HedgePosition = { ticker: s.ticker, qty: s.qty, label, groupId, color: s.isCall ? callColor : putColor };
                        return HedgePositionModel.fromDto(dto, this._optionsChainService);
                    }));
                } catch {
                    return { applied: false, reason: 'unplaceable-leg' };
                }
            }

            // Guard the await window: if a concurrent grid action dropped a matched target leg, bail
            // before mutating anything (still all-or-none).
            for (const existing of modOrder) {
                if (this.positions.indexOf(existing) === -1 || !this.isOwnedLeg(existing)) {
                    return { applied: false, reason: 'missing-target-leg' };
                }
            }

            // PHASE 2 — apply synchronously (cannot fail). Track everything for un-Preview.
            const added: HedgePositionModel[] = [];
            addModels.forEach(m => {
                this.positions.push(m);
                added.push(m);
            });

            const modifiedRefs: HedgePositionModel[] = [];
            modOrder.forEach(existing => {
                const res = resByLeg.get(existing);
                if (!res.hasClose && res.delta === 0) {
                    return;   // net no-op on this leg — leave it untouched
                }
                // Capture the PRISTINE baseline once per leg (shared across Solutions) before first mutation.
                if (!this._copilotLegBaseline.has(existing)) {
                    this._copilotLegBaseline.set(existing, this.captureOverlayPhoto(existing));
                }
                modifiedRefs.push(existing);
                const newAbs = Math.abs(existing.qty) + res.delta;
                if (res.hasClose || newAbs <= 0) {
                    existing.removed = true;                      // close the whole position (or decreased to <= 0)
                } else {
                    existing.transQty = newAbs;                  // increase/decrease -> new magnitude (asDto re-signs by side)
                    existing.selectedForTrans = true;
                }
            });

            if (added.length > 0) {
                this._copilotLedger.recordAdded(solutionId, added);
            }
            if (modifiedRefs.length > 0) {
                this._copilotModifiedLegs[solutionId] = modifiedRefs.slice();
            }
            this.refreshLegsSuppressingDivergence('Both');   // CoPilot's own repaint — must not trip the divergence warning
            if (added.length > 0) {
                this.updateQuoteSubscriptions(added, true);
            }
            this._changeDetector.detectChanges();
            return { applied: true };
        } finally {
            this._copilotApplyInFlight.delete(solutionId);
        }
    }

    // --- un-Preview a Solution -----------------------------------------------------------
    // Reverse EXACTLY what this Solution's Preview wrote (FR-77/78/79/145): hard-splice the CoPilot-ADDED
    // legs by reference (the 'open' legs CoPilot added), and restore each MODIFIED owned leg to its PRISTINE
    // baseline — but only when no OTHER still-Previewed Solution also modifies that leg, so a partial
    // un-Preview never wipes a leg another Solution still owns and a full teardown always returns every
    // touched leg to pristine (no residue). Only OWNED legs are ever modified, so user-authored legs are
    // untouched by construction (FR-79). Never routes through the commit path.
    async removeCopilotLegs(solutionId: SolutionId): Promise<void> {
        const addedLegs = this._copilotLedger.removeSolution(solutionId);
        const modifiedLegs = this._copilotModifiedLegs[solutionId] || [];
        delete this._copilotModifiedLegs[solutionId];   // delete first so the "other Solution" scan excludes this one

        const hasAdded = !isVoid(addedLegs) && addedLegs.length > 0;
        if (!hasAdded && modifiedLegs.length === 0) {
            return;
        }

        modifiedLegs.forEach(leg => {
            if (this.isLegModifiedByAnotherSolution(leg)) {
                return;   // defensive: the M3-4 OQ-13 block makes this impossible (<=1 Solution per owned leg)
            }
            const baseline = this._copilotLegBaseline.get(leg);
            if (baseline) {
                leg.transQty = baseline.transQty;
                leg.removed = baseline.removed;
                leg.selectedForTrans = baseline.selectedForTrans;
                leg.selectedForMerge = baseline.selectedForMerge;
                leg.selectedForHarvest = baseline.selectedForHarvest;
                this._copilotLegBaseline.delete(leg);
            }
        });

        // Hard-splice exactly the CoPilot-added legs by reference.
        if (hasAdded) {
            this.positions = this.positions.filter(x => addedLegs.indexOf(x) === -1);
            this.updateQuoteSubscriptions(addedLegs, false, true);
        }

        this.refreshLegsSuppressingDivergence('Both');   // CoPilot's own repaint — must not trip the divergence warning
        this._changeDetector.detectChanges();
        // NOTE (FR-103/NFR-23): re-arm is driven by the child via resetCopilotDivergence() on a TRUE
        // Conversation boundary (new-conversation / portfolio-switch; reload re-inits the component) — NOT
        // inferred from ledger emptiness here, so a re-run that happens to empty the ledger does not re-arm
        // mid-Conversation ("warn exactly once per Conversation").
    }

    private isLegModifiedByAnotherSolution(leg: HedgePositionModel): boolean {
        const ids = Object.keys(this._copilotModifiedLegs);
        for (const id of ids) {
            if (this._copilotModifiedLegs[id].indexOf(leg) !== -1) {
                return true;
            }
        }
        return false;
    }

    // OQ-13 companion to isLegModifiedByAnotherSolution, matched by full ticker instead of model reference:
    // catches the case where a prior Solution CLOSED the owned leg (removed=true => no longer "owned", so the
    // owned-match misses it). Lets applySolution report hedge-locked rather than missing-target for a hedge
    // that is in the book but already being modified by another previewed Solution.
    private isTickerModifiedByAnotherSolution(ticker: string): boolean {
        const ids = Object.keys(this._copilotModifiedLegs);
        for (const id of ids) {
            const legs = this._copilotModifiedLegs[id];
            for (const leg of legs) {
                if (leg.getTicker() === ticker) {
                    return true;
                }
            }
        }
        return false;
    }

    // Snapshot an owned leg's mutable overlay before CoPilot modifies it (clone() is lossy — capture by value).
    private captureOverlayPhoto(leg: HedgePositionModel): CopilotLegOverlayPhoto {
        return {
            transQty: leg.transQty,
            removed: leg.removed,
            selectedForTrans: leg.selectedForTrans,
            selectedForMerge: leg.selectedForMerge,
            selectedForHarvest: leg.selectedForHarvest
        };
    }

    // refreshLegs wrapper that SUPPRESSES the divergence check for a NON-user repaint: CoPilot's own
    // Preview/un-Preview, AND the programmatic commit/sync reloads (onAfterStateApplied, onHedgeRemoved)
    // that preserve CoPilot leg references (so the by-reference presence test alone would false-positive).
    // Only a genuine USER manual edit should warn.
    private refreshLegsSuppressingDivergence(side: 'Calls' | 'Call' | 'Puts' | 'Put' | 'Calls & Puts' | 'Both'): void {
        this._suppressDivergenceCheck = true;
        try {
            this.refreshLegs(side);
        } finally {
            this._suppressDivergenceCheck = false;
        }
    }

    // M3-6: the divergence check, run at the END of refreshLegs (the single repaint choke point every leg
    // add/remove/resize passes through). Warns ONCE when a USER manual leg edit lands while CoPilot-suggested
    // legs are present; the edit is never prevented (NFR-23). 'CoPilot legs present' is tested by model
    // REFERENCE in the CURRENT working state: a USER edit preserves those references (in-place / filter /
    // concat), but a wholesale reload (portfolio load, matrix commit, clear/reload) builds NEW models, so the
    // references vanish and those paths self-exclude. _suppressDivergenceCheck (set by
    // refreshLegsSuppressingDivergence) excludes CoPilot's own Preview/un-Preview + the commit/sync repaints.
    private checkCopilotDivergence(): void {
        if (this._suppressDivergenceCheck || !this._copilotDivergenceArmed) {
            return;   // non-user repaint (CoPilot / commit / sync), or already warned this Conversation (NFR-23 once)
        }
        const addedPresent = this._copilotLedger.allCopilotLegs().some(l => this.positions.indexOf(l) !== -1);
        const modifiedPresent = Object.keys(this._copilotModifiedLegs).some(
            id => this._copilotModifiedLegs[id].some(l => this.positions.indexOf(l) !== -1));
        if (!addedPresent && !modifiedPresent) {
            return;   // FR-97: nothing to diverge from while no CoPilot legs are on the working state
        }
        this._copilotDivergenceArmed = false;   // warn exactly once per Conversation (NFR-23)
        this._toastr.warning(
            'You are making manual changes on top of the ORION-suggested hedges. You are now leaving the ORION-suggested state — your changes are custom.',
            'Custom changes',
            { closeButton: true, disableTimeOut: true }
        );
    }

    // FR-103/NFR-23 re-arm. Part of IHedgingGridCopilotFacade: the child calls this ONLY on a true
    // Conversation boundary (new-conversation, portfolio-switch; reload re-inits the component). A re-run
    // within a Conversation does NOT call it, so the warning stays "once per Conversation".
    resetCopilotDivergence(): void {
        this._copilotDivergenceArmed = true;
    }

    // The underlying instrument being hedged — drives the CoPilot questionnaire's ets-strikes-dropdown.
    readTargetInstrument(): TradingInstrument | null {
        return this.tradingInstrument || null;
    }

    // The NEAREST AVAILABLE option expiration of the underlying (its strike ladder drives the CoPilot
    // questionnaire's ets-strikes-dropdown). Date-independent (owner rule 2026-06-21) — mirrors the Hedge
    // Matrix's getNearestExpiration (hedge-matrix-data.service.ts:388): the first VISIBLE (non-expired)
    // expiration in the chain. Returns null when no instrument / chain is available.
    async resolveNearestStrikeExpiration(): Promise<OptionExpirationDescriptor | null> {
        const inst = this.tradingInstrument;
        if (isVoid(inst) || isVoid(inst.ticker)) {
            return null;
        }
        const chain = await this._optionsChainService.getChain(inst.ticker);
        const expirations = (chain && chain.expirations) ? chain.expirations : [];
        const nearest = expirations.filter(x => x.visible)[0];
        return nearest || null;
    }

    // Part of IHedgingGridCopilotFacade (the tradeable-date universe that restricts the CoPilot Select
    // tab's date-boxes). VESTIGIAL on the Pad — the CoPilot's live ctx is HedgingGridComponent, which
    // reads the Matrix's already-cached chain synchronously; the Pad has no such cache (its chain read is
    // async). Present only to satisfy the interface for the deferred Select/Adjust delegation. Mirrors the
    // other Pad facade stubs (isCopilotHedgeModified etc.) — never invoked on this ctx today.
    readAvailableExpirations(): string[] {
        return [];
    }

    // CR-20260804-001 atmOffset anchors — vestigial on the Pad (the card's ctx is the host
    // facade, not the Pad); present to satisfy IHedgingGridCopilotFacade.
    readChosenAtm(): number | null {
        return null;
    }

    readDynamicAtm(): number | null {
        return null;
    }

    // Vestigial on the Pad for the same reason (the embedded Select Copilot lives on the
    // host facade, never on the Pad's).
    readAvailableExpirationDescriptors(): OptionExpirationDescriptor[] {
        return [];
    }

    // CR-20260924-002 (ORION ES mode) — vestigial on the Pad for the same reason: the ES toggle
    // lives on the ORION window, whose ctx is HedgingGridComponent. The Pad never runs in ES mode.
    readOrionEsMode(): boolean {
        return false;
    }

    setOrionEsMode(_on: boolean): void {
    }

    readMatrixEsHedgingEnabled(): boolean {
        return false;
    }

    readEsContractCandidates(): null {
        return null;
    }

    readEsExpirationDescriptors(_contract: string): OptionExpirationDescriptor[] {
        return [];
    }

    // Per-leg ES->SPX normFactor for the CoPilot Solution-card net price (mirrors getNormFactor in the
    // hedging-grid sections). Base = the loaded portfolio's asset. 1 for SPX/base or a void asset; 0.5 for
    // ES futures. DISTINCT from readDefaultQty (quantity normalizer). NOTE: vestigial on the Pad — the card's
    // ctx is the host (HedgingGridComponent) facade, not the Pad; present to satisfy IHedgingGridCopilotFacade
    // for the deferred Select/Adjust delegation.
    getNormFactor(asset: string): number {
        const base = this._selectedPortfolio ? this._selectedPortfolio.asset : null;
        if (!asset || !base || asset === base) {
            return 1;
        }
        return this._normalizationService.getPointValueRatio(asset, base);
    }

    // The loaded portfolio's default order quantity — the HCF/defaultQty denominator for the CoPilot
    // Solution-card net price. undefined until the @Input is set.
    readDefaultQty(): number | undefined {
        return this.portfolioDefaultQty;
    }

    // Part of IHedgingGridCopilotFacade, and vestigial on the Pad for the same reason getNormFactor
    // above is: the live copilot ctx is HedgingGridComponent (the Matrix), not the Pad. It reads the
    // same portfolio field that getNormFactor takes its base from, so the two cannot disagree if the
    // Pad ever does become the ctx.
    readBaseAsset(): string | null {
        return this._selectedPortfolio ? (this._selectedPortfolio.asset || null) : null;
    }

    // Part of IHedgingGridCopilotFacade; the live card ctx is HedgingGridComponent (the Matrix), not the Pad,
    // and the Pad does not track per-hedge user modification — report none. Select/Adjust revisit when they ship.
    isCopilotHedgeModified(_groupId: string): boolean {
        return false;
    }

    isCopilotHedgeGone(_tickers: string[]): boolean {
        return false;
    }

}
